import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, TrendingDown, Plus, Trash2, RefreshCw, Loader2, Edit2, Check, X, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronUp, Lock, Smartphone, Monitor, Shield, KeyRound, MapPin, User, Settings, LogOut, Eye, EyeOff, Save, MoveUp, MoveDown, DollarSign, Maximize2, Minimize2, Mail, Clock, LayoutDashboard, ImageIcon, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import { portfolioAPI } from '../utils/api';
import axios from 'axios';
import Footer from './Footer';
import InactivityWarning from './InactivityWarning';
import VersionChangelog from './VersionChangelog';
import { 
  WorldClocks, 
  NewsTicker, 
  MarketMoversPanel, 
  MarketNewsPanel,
  PlatformPerformancePanel,
  StockAnalyticsPanel,
  PerformanceLeadersPanel,
  YourStocksPanel,
  PortfolioDiversityPanel,
  NonMarketAssetsPanel
} from './dashboard';

const PortfolioDashboard = () => {
  const navigate = useNavigate();
  const [stocks, setStocks] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newStock, setNewStock] = useState({ symbol: '', shares: '', costBasis: '', platform: '', currentPrice: '', investmentType: 'STOCK' });
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingStock, setIsAddingStock] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Platform sorting state
  const [platformSortColumn, setPlatformSortColumn] = useState(null);
  const [platformSortDirection, setPlatformSortDirection] = useState('asc');
  
  // Analytics sorting state
  const [analyticsSortColumn, setAnalyticsSortColumn] = useState(null);
  const [analyticsSortDirection, setAnalyticsSortDirection] = useState('asc');
  
  // Movers panel state
  const [showMovers, setShowMovers] = useState(() => {
    const saved = localStorage.getItem('portfolio_showMovers');
    return saved !== null ? saved === 'true' : false;
  });
  const [moversPeriod, setMoversPeriod] = useState('1D'); // '1D', '1W', '1M', '1Y'
  const [marketLivePeriod, setMarketLivePeriod] = useState('1D'); // '1D', '1W', '1M', '1Y', 'ALL'
  const [showMoversChart, setShowMoversChart] = useState(() => {
    const saved = localStorage.getItem('portfolio_showMoversChart');
    return saved !== null ? saved === 'true' : false;
  });
  
  // Panel visibility - persisted in localStorage
  const [showStockList, setShowStockList] = useState(() => {
    const saved = localStorage.getItem('portfolio_showStockList');
    return saved !== null ? saved === 'true' : true;
  });
  const [showPlatformPerformance, setShowPlatformPerformance] = useState(() => {
    const saved = localStorage.getItem('portfolio_showPlatformPerformance');
    return saved !== null ? saved === 'true' : true;
  });
  const [showStockAnalytics, setShowStockAnalytics] = useState(() => {
    const saved = localStorage.getItem('portfolio_showStockAnalytics');
    return saved !== null ? saved === 'true' : false;
  });
  const [showDiversity, setShowDiversity] = useState(() => {
    const saved = localStorage.getItem('portfolio_showDiversity');
    return saved !== null ? saved === 'true' : true;
  });
  const [showMarketPerformance, setShowMarketPerformance] = useState(() => {
    const saved = localStorage.getItem('portfolio_showMarketPerformance');
    return saved !== null ? saved === 'true' : true;
  });
  const [showMarketLive, setShowMarketLive] = useState(() => {
    const saved = localStorage.getItem('portfolio_showMarketLive');
    return saved !== null ? saved === 'true' : true;
  });
  
  // Market Live expanded/fullscreen mode
  const [isMarketLiveExpanded, setIsMarketLiveExpanded] = useState(false);
  // Non-Market Assets expanded/fullscreen mode
  const [isNonMarketExpanded, setIsNonMarketExpanded] = useState(false);
  // Store previous showMarketLive state before expanding
  const [wasMarketLiveShown, setWasMarketLiveShown] = useState(true);
  // Mobile Market Live view mode: 'bubbles' or 'panels' (movers/news)
  const [mobileMarketView, setMobileMarketView] = useState('bubbles');
  // Selected stock for mobile popup
  const [mobileSelectedStock, setMobileSelectedStock] = useState(null);
  // Mobile panel view modes: 'market' or 'portfolio'
  const [mobileMoversMode, setMobileMoversMode] = useState('market');
  const [mobileNewsMode, setMobileNewsMode] = useState('market');
  
  // Privacy mode - hide dollar amounts (legacy, kept for other components)
  const [privacyMode, setPrivacyMode] = useState(() => {
    const saved = localStorage.getItem('portfolio_privacyMode');
    return saved !== null ? saved === 'true' : false;
  });
  
  // Hide Market Value toggle - independent control for Market Value visibility
  const [hideMarketValue, setHideMarketValue] = useState(() => {
    const saved = localStorage.getItem('portfolio_hideMarketValue');
    return saved !== null ? saved === 'true' : false;
  });
  
  // Hide Total Value toggle - independent control for Total Value visibility
  const [hideTotalValue, setHideTotalValue] = useState(() => {
    const saved = localStorage.getItem('portfolio_hideTotalValue');
    return saved !== null ? saved === 'true' : false;
  });
  
  // Market-only totals mode - show only market assets in total (legacy)
  const [marketOnlyTotals, setMarketOnlyTotals] = useState(() => {
    const saved = localStorage.getItem('portfolio_marketOnlyTotals');
    return saved !== null ? saved === 'true' : false;
  });
  
  // Toggle privacy mode (hides all dollar amounts in other panels)
  const togglePrivacyMode = () => {
    setPrivacyMode(prev => {
      const newValue = !prev;
      localStorage.setItem('portfolio_privacyMode', newValue.toString());
      return newValue;
    });
  };
  
  // Toggle Market Value visibility in header (hides market-related values only)
  const toggleHideMarketValue = () => {
    const newValue = !hideMarketValue;
    setHideMarketValue(newValue);
    localStorage.setItem('portfolio_hideMarketValue', newValue.toString());
  };
  
  // Toggle Total Value visibility in header (controls all-asset value visibility)
  const toggleHideTotalValue = () => {
    const newValue = !hideTotalValue;
    setHideTotalValue(newValue);
    localStorage.setItem('portfolio_hideTotalValue', newValue.toString());
    // Sync privacy mode so all panels hide/show values
    setPrivacyMode(newValue);
    localStorage.setItem('portfolio_privacyMode', newValue.toString());
  };
  
  // Toggle market-only totals mode (legacy)
  const toggleMarketOnlyTotals = () => {
    setMarketOnlyTotals(prev => {
      const newValue = !prev;
      localStorage.setItem('portfolio_marketOnlyTotals', newValue.toString());
      return newValue;
    });
  };
  
  // Update auto-logout timeout
  const updateAutoLogoutMinutes = (minutes) => {
    const value = parseInt(minutes, 10);
    if (value >= 1 && value <= 60) {
      setAutoLogoutMinutes(value);
      localStorage.setItem('portfolio_autoLogoutMinutes', value.toString());
    }
  };
  
  // Import/Export state
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  
  // Format currency with privacy mode support (hides when Total Value toggle is on)
  const formatCurrencyPrivate = (value) => {
    if (privacyMode) return '•••••';
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Format currency for market-only values (hides when Market Value toggle is on)
  const formatCurrencyMarket = (value) => {
    if (hideMarketValue) return '•••••';
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };
  
  // Panel order state - persisted in localStorage
  const defaultPanelOrder = ['marketlive', 'diversity', 'platform', 'analytics', 'leaders', 'stocks', 'nonmarket'];
  const [panelOrder, setPanelOrder] = useState(() => {
    const saved = localStorage.getItem('portfolio_panelOrder');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Ensure marketlive panel is included if not present
        if (!parsed.includes('marketlive')) {
          parsed.unshift('marketlive');
        }
        // Ensure diversity panel is included if not present
        if (!parsed.includes('diversity')) {
          const marketliveIndex = parsed.indexOf('marketlive');
          parsed.splice(marketliveIndex + 1, 0, 'diversity');
        }
        // Ensure nonmarket panel is included if not present
        if (!parsed.includes('nonmarket')) {
          parsed.push('nonmarket');
        }
        return parsed;
      } catch {
        return defaultPanelOrder;
      }
    }
    return defaultPanelOrder;
  });
  
  // Move panel up in the order
  const movePanelUp = (panelId) => {
    setPanelOrder((items) => {
      const index = items.indexOf(panelId);
      if (index > 0) {
        const newOrder = [...items];
        [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
        localStorage.setItem('portfolio_panelOrder', JSON.stringify(newOrder));
        return newOrder;
      }
      return items;
    });
  };
  
  // Move panel down in the order
  const movePanelDown = (panelId) => {
    setPanelOrder((items) => {
      const index = items.indexOf(panelId);
      if (index < items.length - 1) {
        const newOrder = [...items];
        [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
        localStorage.setItem('portfolio_panelOrder', JSON.stringify(newOrder));
        return newOrder;
      }
      return items;
    });
  };
  
  const [performance, setPerformance] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [selectedStockPopup, setSelectedStockPopup] = useState(null); // For mobile stock detail popup
  const { toast } = useToast();
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [refreshingStockId, setRefreshingStockId] = useState(null);
  const [recentlyUpdatedStocks, setRecentlyUpdatedStocks] = useState(new Set()); // Track stocks that just updated for animation
  const [moversSortColumn, setMoversSortColumn] = useState('symbol');
  const [moversSortDirection, setMoversSortDirection] = useState('desc');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [marketIndex, setMarketIndex] = useState(null); // S&P 500 data
  const [usIndices, setUsIndices] = useState(null); // All US indices (S&P, NASDAQ, DOW)
  const [refreshIndex, setRefreshIndex] = useState(0); // Current stock being refreshed
  const [isRotatingRefresh, setIsRotatingRefresh] = useState(false);
  
  // Fullscreen Market Live data
  const [marketNews, setMarketNews] = useState([]);
  const [portfolioNews, setPortfolioNews] = useState([]);  // News specific to user's stocks
  const [tickerNews, setTickerNews] = useState([]);  // Separate news for ticker (crypto, forex, alerts)
  const [marketMovers, setMarketMovers] = useState({ gainers: [], losers: [] });
  const [footerVersion, setFooterVersion] = useState('v9.0');
  const [footerText, setFooterText] = useState('');
  const [showChangelog, setShowChangelog] = useState(false);
  const [apiRateLimited, setApiRateLimited] = useState({ limited: false, seconds_remaining: 0 });
  
  // Keep market movers sorted by change percent (re-sorts when data updates)
  // Preserve all metadata fields (loading, loadingProgress, etc.)
  // Sync market movers with portfolio data so percentages match the bubbles
  const sortedMarketMovers = useMemo(() => {
    // Create a map of portfolio stocks for quick lookup
    const portfolioMap = new Map();
    if (stocks && stocks.length > 0) {
      stocks.forEach(s => {
        if (s.symbol && s.dailyChangePercent !== null && s.dailyChangePercent !== undefined) {
          portfolioMap.set(s.symbol, {
            changePercent: s.dailyChangePercent,
            price: s.currentPrice,
            change: s.dailyChange
          });
        }
      });
    }
    
    // Update market movers with portfolio data where available (so they match bubbles)
    const syncWithPortfolio = (movers) => {
      return movers.map(stock => {
        const portfolioData = portfolioMap.get(stock.symbol);
        if (portfolioData) {
          // Use portfolio data to ensure consistency with bubbles
          return {
            ...stock,
            changePercent: portfolioData.changePercent,
            price: portfolioData.price || stock.price,
            change: portfolioData.change || stock.change
          };
        }
        return stock;
      });
    };
    
    const gainers = syncWithPortfolio([...(marketMovers?.gainers || [])])
      .sort((a, b) => (b.changePercent || 0) - (a.changePercent || 0));
    const losers = syncWithPortfolio([...(marketMovers?.losers || [])])
      .sort((a, b) => (a.changePercent || 0) - (b.changePercent || 0));
    return { 
      gainers, 
      losers,
      // Preserve loading metadata
      loading: marketMovers?.loading,
      loadingProgress: marketMovers?.loadingProgress,
      totalStocksAnalyzed: marketMovers?.totalStocksAnalyzed,
      totalStocksTarget: marketMovers?.totalStocksTarget,
      rateLimited: marketMovers?.rateLimited,
      rateLimitSecondsRemaining: marketMovers?.rateLimitSecondsRemaining,
      cacheAge: marketMovers?.cacheAge,
      note: marketMovers?.note
    };
  }, [marketMovers, stocks]);
  
  // Calculate portfolio movers from user's stocks
  const portfolioMovers = useMemo(() => {
    if (!stocks || stocks.length === 0) {
      return { gainers: [], losers: [] };
    }
    
    // Map stocks to mover format with all needed fields
    const stocksWithChange = stocks
      .filter(s => s.dailyChangePercent !== null && s.dailyChangePercent !== undefined)
      .map(s => ({
        symbol: s.symbol,
        name: s.name || s.symbol,
        price: s.currentPrice || 0,
        change: s.dailyChange || 0,
        changePercent: s.dailyChangePercent || 0,
        dayHigh: s.dayHigh || s.currentPrice,
        dayLow: s.dayLow || s.currentPrice,
        week52High: s.week52High || s.currentPrice,
        previousClose: s.previousClose || s.currentPrice,
        openPrice: s.openPrice || s.currentPrice,
      }));
    
    // Sort and get top 15 gainers and losers
    const gainers = stocksWithChange
      .filter(s => s.changePercent > 0)
      .sort((a, b) => b.changePercent - a.changePercent)
      .slice(0, 15);
    
    const losers = stocksWithChange
      .filter(s => s.changePercent < 0)
      .sort((a, b) => a.changePercent - b.changePercent)
      .slice(0, 15);
    
    return { gainers, losers };
  }, [stocks]);
  
  // Use refs to access current state in setInterval callback
  const stocksRef = useRef(stocks);
  const refreshIndexRef = useRef(0);
  
  // Keep refs in sync with state
  useEffect(() => {
    stocksRef.current = stocks;
  }, [stocks]);
  
  // Fetch S&P 500 index data based on selected period
  const fetchMarketIndex = async (period = '1D') => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market/index?period=${period}`);
      const data = await response.json();
      if (data.success && data.index) {
        setMarketIndex(data.index);
      }
    } catch (error) {
      console.error('Error fetching market index:', error);
    }
  };
  
  // Fetch all US indices (S&P 500, NASDAQ, DOW) from Yahoo Finance
  const fetchUsIndices = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market/us-indices`);
      const data = await response.json();
      if (data.success && data.indices) {
        setUsIndices(data.indices);
      }
    } catch (error) {
      console.error('Error fetching US indices:', error);
    }
  };
  
  // Rotating refresh - refreshes one stock at a time every second
  const refreshNextStock = async () => {
    const currentStocks = stocksRef.current;
    if (!isAuthenticated || currentStocks.length === 0) return;
    
    const userEmail = sessionStorage.getItem('portfolio_user_email');
    const currentIndex = refreshIndexRef.current;
    const stockToRefresh = currentStocks[currentIndex % currentStocks.length];
    
    if (!stockToRefresh) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/portfolio/${stockToRefresh.id}/refresh?user_email=${encodeURIComponent(userEmail)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      const data = await response.json();
      
      if (data.success && data.stock) {
        const newStock = data.stock;
        
        // Check if the price actually changed before animating
        setStocks(prevStocks => {
          const oldStock = prevStocks.find(s => s.id === newStock.id);
          const priceChanged = oldStock && (
            oldStock.currentPrice !== newStock.currentPrice ||
            oldStock.dailyChangePercent !== newStock.dailyChangePercent
          );
          
          // Only animate if price actually changed
          if (priceChanged) {
            setRecentlyUpdatedStocks(prev => new Set([...prev, newStock.id]));
            
            // Remove from recently updated after animation completes (1 second)
            setTimeout(() => {
              setRecentlyUpdatedStocks(prev => {
                const newSet = new Set(prev);
                newSet.delete(newStock.id);
                return newSet;
              });
            }, 1000);
          }
          
          return prevStocks.map(s => 
            s.id === newStock.id ? { ...s, ...newStock } : s
          );
        });
      }
    } catch (error) {
      console.error(`Error refreshing ${stockToRefresh.symbol}:`, error);
    }
    
    // Move to next stock using ref to avoid stale closure
    refreshIndexRef.current = currentIndex + 1;
    setRefreshIndex(refreshIndexRef.current); // Also update state for UI display
  };
  
  // Update current time every minute for market status
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    return () => clearInterval(timer);
  }, []);
  
  // Check if US stock market is open (NYSE/NASDAQ: 9:30 AM - 4:00 PM ET, Mon-Fri)
  const getMarketStatus = () => {
    const now = currentTime;
    // Convert to Eastern Time
    const etOptions = { timeZone: 'America/New_York', hour: 'numeric', minute: 'numeric', hour12: false };
    const etTimeStr = now.toLocaleString('en-US', etOptions);
    const [hours, minutes] = etTimeStr.split(':').map(Number);
    const etMinutes = hours * 60 + minutes;
    
    const day = now.toLocaleString('en-US', { timeZone: 'America/New_York', weekday: 'short' });
    const isWeekend = day === 'Sat' || day === 'Sun';
    
    // Get current date in ET for holiday check
    const etDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const month = etDate.getMonth() + 1; // 1-12
    const dateNum = etDate.getDate();
    const year = etDate.getFullYear();
    
    // US Stock Market Holidays (fixed dates and calculated dates)
    // Returns holiday name if today is a holiday, null otherwise
    const getHoliday = () => {
      // Fixed holidays
      if (month === 1 && dateNum === 1) return "New Year's Day";
      if (month === 7 && dateNum === 4) return "Independence Day";
      if (month === 12 && dateNum === 25) return "Christmas Day";
      
      // Juneteenth (June 19)
      if (month === 6 && dateNum === 19) return "Juneteenth";
      
      // MLK Day (3rd Monday of January)
      if (month === 1 && day === 'Mon') {
        const firstDay = new Date(year, 0, 1).getDay();
        const firstMonday = firstDay <= 1 ? (1 - firstDay + 1) : (8 - firstDay + 1);
        const thirdMonday = firstMonday + 14;
        if (dateNum === thirdMonday) return "MLK Day";
      }
      
      // Presidents Day (3rd Monday of February)
      if (month === 2 && day === 'Mon') {
        const firstDay = new Date(year, 1, 1).getDay();
        const firstMonday = firstDay <= 1 ? (1 - firstDay + 1) : (8 - firstDay + 1);
        const thirdMonday = firstMonday + 14;
        if (dateNum === thirdMonday) return "Presidents Day";
      }
      
      // Good Friday (Friday before Easter - approximate check for 2024-2030)
      const goodFridayDates = {
        2024: { month: 3, day: 29 },
        2025: { month: 4, day: 18 },
        2026: { month: 4, day: 3 },
        2027: { month: 3, day: 26 },
        2028: { month: 4, day: 14 },
        2029: { month: 3, day: 30 },
        2030: { month: 4, day: 19 }
      };
      if (goodFridayDates[year] && month === goodFridayDates[year].month && dateNum === goodFridayDates[year].day) {
        return "Good Friday";
      }
      
      // Memorial Day (last Monday of May)
      if (month === 5 && day === 'Mon' && dateNum > 24) return "Memorial Day";
      
      // Labor Day (1st Monday of September)
      if (month === 9 && day === 'Mon' && dateNum <= 7) return "Labor Day";
      
      // Thanksgiving (4th Thursday of November)
      if (month === 11 && day === 'Thu') {
        const firstDay = new Date(year, 10, 1).getDay();
        const firstThursday = firstDay <= 4 ? (4 - firstDay + 1) : (11 - firstDay + 1);
        const fourthThursday = firstThursday + 21;
        if (dateNum === fourthThursday) return "Thanksgiving";
      }
      
      return null;
    };
    
    const holiday = getHoliday();
    
    // Market hours: 9:30 AM (570 min) to 4:00 PM (960 min) ET
    const marketOpen = 9 * 60 + 30; // 9:30 AM
    const marketClose = 16 * 60; // 4:00 PM
    
    // Pre-market: 4:00 AM - 9:30 AM ET
    const preMarketOpen = 4 * 60;
    // After-hours: 4:00 PM - 8:00 PM ET
    const afterHoursClose = 20 * 60;
    
    if (isWeekend) {
      return { status: 'closed', label: 'Market Closed', sublabel: 'Weekend' };
    }
    
    if (holiday) {
      return { status: 'closed', label: 'Market Closed', sublabel: holiday };
    }
    
    if (etMinutes >= marketOpen && etMinutes < marketClose) {
      const closeTime = new Date();
      closeTime.setHours(16, 0, 0, 0);
      return { status: 'open', label: 'Market Open', sublabel: 'Closes 4:00 PM ET' };
    }
    
    if (etMinutes >= preMarketOpen && etMinutes < marketOpen) {
      return { status: 'premarket', label: 'Pre-Market', sublabel: 'Opens 9:30 AM ET' };
    }
    
    if (etMinutes >= marketClose && etMinutes < afterHoursClose) {
      return { status: 'afterhours', label: 'After Hours', sublabel: 'Until 8:00 PM ET' };
    }
    
    return { status: 'closed', label: 'Market Closed', sublabel: 'Opens 9:30 AM ET' };
  };
  
  const marketStatus = getMarketStatus();
  
  // Save panel visibility preferences to localStorage
  const toggleStockList = () => {
    const newValue = !showStockList;
    setShowStockList(newValue);
    localStorage.setItem('portfolio_showStockList', newValue.toString());
  };
  
  const togglePlatformPerformance = () => {
    const newValue = !showPlatformPerformance;
    setShowPlatformPerformance(newValue);
    localStorage.setItem('portfolio_showPlatformPerformance', newValue.toString());
  };
  
  const toggleStockAnalytics = () => {
    const newValue = !showStockAnalytics;
    setShowStockAnalytics(newValue);
    localStorage.setItem('portfolio_showStockAnalytics', newValue.toString());
  };
  
  const toggleDiversity = () => {
    const newValue = !showDiversity;
    setShowDiversity(newValue);
    localStorage.setItem('portfolio_showDiversity', newValue.toString());
  };
  
  const toggleMarketLive = () => {
    const newValue = !showMarketLive;
    setShowMarketLive(newValue);
    localStorage.setItem('portfolio_showMarketLive', newValue.toString());
  };
  
  const toggleMoversChart = () => {
    const newValue = !showMoversChart;
    setShowMoversChart(newValue);
    localStorage.setItem('portfolio_showMoversChart', newValue.toString());
  };
  
  const toggleMovers = () => {
    const newValue = !showMovers;
    setShowMovers(newValue);
    localStorage.setItem('portfolio_showMovers', newValue.toString());
  };
  
  // PIN Authentication - using sessionStorage so it clears when browser closes
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('portfolio_auth') === 'true';
  });
  const [userRole, setUserRole] = useState(() => {
    return sessionStorage.getItem('portfolio_user_role') || 'user';
  });
  const [email, setEmail] = useState(() => {
    return localStorage.getItem('portfolio_email') || '';
  });
  const [rememberEmail, setRememberEmail] = useState(() => {
    return localStorage.getItem('portfolio_remember_email') === 'true';
  });
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  // Request Access modal state
  const [showRequestAccess, setShowRequestAccess] = useState(false);
  const [requestForm, setRequestForm] = useState({ fullName: '', email: '', confirmEmail: '', howFound: '', investments: '' });
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [emailExistsPrompt, setEmailExistsPrompt] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // 2FA state
  const [twoFactorPending, setTwoFactorPending] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorEmail, setTwoFactorEmail] = useState('');
  const [twoFactorData, setTwoFactorData] = useState(null); // store login response data for after 2FA
  const [isVerifying2FA, setIsVerifying2FA] = useState(false);

  // Auto-open Request Access modal when navigated from Learn More page
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('requestAccess') === 'true') {
      setShowRequestAccess(true);
      setRequestSubmitted(false);
      setEmailExistsPrompt(false);
      setRequestForm({ fullName: '', email: '', confirmEmail: '', howFound: '', investments: '' });
      window.history.replaceState({}, '', '/');
    }
  }, []);
  
  // Biometric Authentication state
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricNeverAsk, setBiometricNeverAsk] = useState(false);
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  
  // Check if user is a viewer (read-only access)
  const isViewer = userRole === 'viewer';
  
  // Check if we're emulating a user (admin viewing as user)
  const [emulationInfo, setEmulationInfo] = useState(() => {
    const stored = sessionStorage.getItem('emulated_user');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return null;
      }
    }
    return null;
  });
  
  // Exit emulation and return to admin
  const exitEmulation = () => {
    // Clear emulation data
    sessionStorage.removeItem('emulated_user');
    sessionStorage.removeItem('portfolio_auth');
    sessionStorage.removeItem('portfolio_user_email');
    sessionStorage.removeItem('portfolio_user_role');
    
    // Redirect back to admin
    window.location.href = '/admin';
  };
  
  // Mobile preview mode for testing
  const [mobilePreview, setMobilePreview] = useState(false);
  
  // Profile modal state
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [profileData, setProfileData] = useState(() => {
    // Load from localStorage first for instant UI, then update from API
    const savedShowMarket = localStorage.getItem('showMarketInfo');
    const savedShowNonMarket = localStorage.getItem('showNonMarketAssets');
    const savedLoginBg = localStorage.getItem('loginBackground');
    return {
      displayName: '',
      currentPin: '',
      newPin: '',
      confirmPin: '',
      hideEmailAmounts: false,  // Email privacy setting
      showMarketInfo: savedShowMarket !== null ? savedShowMarket === 'true' : true,
      showNonMarketAssets: savedShowNonMarket !== null ? savedShowNonMarket === 'true' : true,
      loginBackground: savedLoginBg || 'none',
      twoFactorEnabled: false
    };
  });
  const [showCurrentPin, setShowCurrentPin] = useState(false);
  const [showNewPin, setShowNewPin] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [userDisplayName, setUserDisplayName] = useState('');
  
  // Security - Auto-logout timeout (default 25 minutes)
  const [autoLogoutMinutes, setAutoLogoutMinutes] = useState(() => {
    const saved = localStorage.getItem('portfolio_autoLogoutMinutes');
    return saved ? parseInt(saved, 10) : 25;
  });
  
  // Get user's IP address
  const [userIP, setUserIP] = useState('Loading...');
  
  useEffect(() => {
    // Fetch user IP address
    fetch('https://api.ipify.org?format=json')
      .then(res => res.json())
      .then(data => setUserIP(data.ip))
      .catch(() => setUserIP('Unknown'));
  }, []);

  // Biometric availability check and mobile detection
  useEffect(() => {
    // Check if mobile device
    const checkMobile = () => {
      setIsMobileDevice(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Check if WebAuthn is available
    const checkBiometric = async () => {
      try {
        if (window.PublicKeyCredential) {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setBiometricAvailable(available);
          
          // Check if user has biometric enabled
          const dataStr = localStorage.getItem('biometric_data');
          if (dataStr) {
            const data = JSON.parse(dataStr);
            const lastEmail = localStorage.getItem('last_biometric_email');
            if (lastEmail && data[lastEmail]) {
              const hasCredential = !!data[lastEmail].credentialId;
              const neverAsk = data[lastEmail].neverAsk === true;
              setBiometricEnabled(hasCredential);
              setBiometricNeverAsk(neverAsk);
            }
          }
        }
      } catch (e) {
        console.error('Error checking biometric:', e);
        setBiometricAvailable(false);
      }
    };
    checkBiometric();

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Biometric helper functions
  const saveBiometricData = (emailAddr, biometricInfo) => {
    try {
      const data = localStorage.getItem('biometric_data');
      const parsed = data ? JSON.parse(data) : {};
      parsed[emailAddr.toLowerCase()] = biometricInfo;
      localStorage.setItem('biometric_data', JSON.stringify(parsed));
      localStorage.setItem('last_biometric_email', emailAddr.toLowerCase());
    } catch (e) {
      console.error('Error saving biometric data:', e);
    }
  };

  const getBiometricData = (emailAddr) => {
    try {
      const data = localStorage.getItem('biometric_data');
      if (!data) return null;
      const parsed = JSON.parse(data);
      return parsed[emailAddr.toLowerCase()] || null;
    } catch (e) {
      return null;
    }
  };

  // Setup biometric (called after successful password login)
  const setupBiometric = async () => {
    try {
      const userEmail = sessionStorage.getItem('portfolio_user_email');
      if (!userEmail) {
        alert('User not logged in.');
        return;
      }

      // Generate random challenge
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      // Create credential (this triggers the fingerprint/face prompt)
      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: challenge,
          rp: {
            name: "PortfolioASaurus",
            id: window.location.hostname,
          },
          user: {
            id: new Uint8Array(16),
            name: userEmail,
            displayName: userDisplayName || "User",
          },
          pubKeyCredParams: [
            { alg: -7, type: "public-key" },    // ES256
            { alg: -257, type: "public-key" }   // RS256
          ],
          authenticatorSelection: {
            authenticatorAttachment: "platform",  // Use device's built-in authenticator
            userVerification: "required",
          },
          timeout: 60000,
          attestation: "none"
        }
      });

      if (credential) {
        const credId = credential.id;
        const rawIdBase64 = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)));
        
        // Save credential to localStorage
        saveBiometricData(userEmail.toLowerCase(), {
          enabled: true,
          credentialId: credId,
          rawId: rawIdBase64,
          setupDate: new Date().toISOString()
        });
        
        setBiometricEnabled(true);
        setShowBiometricPrompt(false);
        toast({
          title: "Biometric Enabled",
          description: "You can now use fingerprint/Face ID to login!",
        });
      }
    } catch (err) {
      console.error('Biometric setup error:', err);
      toast({
        title: "Setup Failed",
        description: err.message || "Could not set up biometric login",
        variant: "destructive"
      });
    }
  };

  // Handle biometric login
  const handleBiometricLogin = async () => {
    try {
      const dataStr = localStorage.getItem('biometric_data');
      const data = dataStr ? JSON.parse(dataStr) : {};
      const lastEmail = localStorage.getItem('last_biometric_email');
      
      if (!lastEmail || !data[lastEmail]) {
        setLoginError('No biometric data found. Please login with password first.');
        return;
      }
      
      const userData = data[lastEmail];
      const { rawId } = userData;
      
      if (!rawId) {
        setLoginError('Biometric credentials incomplete. Please set up again.');
        return;
      }
      
      // Generate a challenge
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      // Decode the raw ID from base64
      let rawIdArray;
      try {
        rawIdArray = Uint8Array.from(atob(rawId), c => c.charCodeAt(0));
      } catch (decodeErr) {
        setLoginError('Error with stored credentials. Please set up again.');
        return;
      }

      // Request biometric authentication (triggers fingerprint/face prompt)
      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: challenge,
          allowCredentials: [{
            id: rawIdArray,
            type: 'public-key'
          }],
          timeout: 60000,
          userVerification: "preferred"
        }
      });

      if (assertion) {
        // Biometric verified! Now call backend to get user data
        setIsLoggingIn(true);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/biometric-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: lastEmail })
        });
        
        const responseData = await response.json();
        
        if (responseData.success) {
          // Check if user is admin - redirect to admin panel
          if (responseData.isAdmin || responseData.email === 'superadmin@admin.com') {
            sessionStorage.setItem('admin_auth', 'true');
            sessionStorage.setItem('admin_email', responseData.email);
            navigate('/admin');
            return;
          }
          
          setIsAuthenticated(true);
          sessionStorage.setItem('portfolio_auth', 'true');
          sessionStorage.setItem('portfolio_user_email', responseData.email);
          
          // Privacy: hide all values and collapse panels on login
          collapseAllOnLogin();
          
          // Try to load cached data for instant display (mobile optimization)
          const cached = loadFromCache(responseData.email);
          if (cached && cached.stocks && cached.stocks.length > 0) {
            // Show cached data immediately
            setStocks(cached.stocks);
            setPerformance(cached.performance);
            setLastUpdate(new Date(cached.timestamp));
            setIsLoading(false);
            // Fetch fresh data in background (will update cache when done)
          } else {
            // No cache - show loading state
            setStocks([]);
            setPerformance(null);
            setIsLoading(true);
          }
          
          // Store user role (viewer, user, admin)
          const role = responseData.role || 'user';
          setUserRole(role);
          sessionStorage.setItem('portfolio_user_role', role);
          
          setLoginError('');
          toast({
            title: "Welcome Back!",
            description: "Biometric login successful",
          });
        } else {
          setLoginError(responseData.message || 'Login failed');
        }
        setIsLoggingIn(false);
      }
    } catch (err) {
      console.error('Biometric login error:', err);
      if (err.name === 'NotAllowedError') {
        setLoginError('Biometric cancelled. Try again or use password.');
      } else if (err.name === 'InvalidStateError') {
        setLoginError('Biometric not found. Please reset and set up again.');
      } else {
        setLoginError(`Error: ${err.message || err.name}. Try using password.`);
      }
      setIsLoggingIn(false);
    }
  };

  // Skip biometric setup
  const skipBiometric = () => {
    setShowBiometricPrompt(false);
  };

  // Never ask for biometric again
  const neverAskBiometric = () => {
    const userEmail = sessionStorage.getItem('portfolio_user_email');
    if (userEmail) {
      saveBiometricData(userEmail.toLowerCase(), {
        enabled: false,
        neverAsk: true
      });
      setBiometricNeverAsk(true);
    }
    setShowBiometricPrompt(false);
  };

  // Reset biometric (for settings)
  const resetBiometric = () => {
    const userEmail = sessionStorage.getItem('portfolio_user_email');
    if (userEmail) {
      const biometricData = localStorage.getItem('biometric_data');
      if (biometricData) {
        const data = JSON.parse(biometricData);
        delete data[userEmail.toLowerCase()];
        localStorage.setItem('biometric_data', JSON.stringify(data));
      }
      setBiometricEnabled(false);
      setBiometricNeverAsk(false);
      toast({
        title: "Biometric Reset",
        description: "Fingerprint/Face ID login has been disabled",
      });
    }
  };

  // Fetch user display name when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // fetchProfile already gets display name, no need for duplicate call
      fetchProfile();
    }
  }, [isAuthenticated]);

  // Fetch footer version on component mount (for login page)
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`)
      .then(res => res.json())
      .then(data => {
        if (data.appVersion) {
          setFooterVersion(`v${data.appVersion}`);
        }
        if (data.settings?.footer?.text) {
          setFooterText(data.settings.footer.text);
        }
      })
      .catch(err => console.error('Error fetching footer:', err));
  }, []);

  // Fetch portfolio when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchPortfolio();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isAuthenticated) {
        fetchPortfolio();
        fetchPerformance();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Start rotating refresh when authenticated
  useEffect(() => {
    if (isAuthenticated && stocks.length > 0) {
      // Fetch performance data in parallel with market data
      fetchPerformance();
      // Fetch market index on login with current period
      fetchMarketIndex(marketLivePeriod);
      // Fetch US indices (S&P 500, NASDAQ, DOW) from Yahoo Finance
      fetchUsIndices();
      
      // Refresh market index every 30 seconds with current period
      const indexTimer = setInterval(() => fetchMarketIndex(marketLivePeriod), 30000);
      // Refresh US indices every 60 seconds (Yahoo Finance data)
      const usIndicesTimer = setInterval(fetchUsIndices, 60000);
      
      // Rotating refresh - 1 stock per second (60 stocks/min, within API limit)
      const refreshTimer = setInterval(refreshNextStock, 1000);
      setIsRotatingRefresh(true);
      
      return () => {
        clearInterval(indexTimer);
        clearInterval(usIndicesTimer);
        clearInterval(refreshTimer);
        setIsRotatingRefresh(false);
      };
    }
  }, [isAuthenticated, stocks.length]);

  // Re-fetch market index when marketLivePeriod changes
  useEffect(() => {
    if (isAuthenticated && stocks.length > 0) {
      fetchMarketIndex(marketLivePeriod);
    }
  }, [marketLivePeriod]);

  // ESC key handler for expanded Market Live panel and Non-Market Assets
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isMarketLiveExpanded) {
          setIsMarketLiveExpanded(false);
        }
        if (isNonMarketExpanded) {
          setIsNonMarketExpanded(false);
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMarketLiveExpanded, isNonMarketExpanded]);

  // Fetch market news and movers when fullscreen is opened (only once initially)
  useEffect(() => {
    if (isMarketLiveExpanded && marketNews.length === 0) {
      // Fetch market news for the news panel
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market/news`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.news) {
            setMarketNews(data.news);
          }
        })
        .catch(err => console.error('Error fetching news:', err));
      
      // Fetch ticker news (crypto, forex, alerts) for the scrolling ticker
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market/ticker-news`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.news) {
            setTickerNews(data.news);
          }
        })
        .catch(err => console.error('Error fetching ticker news:', err));
      
      // Fetch market movers
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market/movers`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.movers) {
            setMarketMovers(data.movers);
          }
        })
        .catch(err => console.error('Error fetching movers:', err));
      
      // Fetch app version for display
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`)
        .then(res => res.json())
        .then(data => {
          if (data.appVersion) {
            setFooterVersion(`v${data.appVersion}`);
          }
        })
        .catch(err => console.error('Error fetching version:', err));
    }
  }, [isMarketLiveExpanded, marketNews.length]);

  // Auto-poll market movers while loading (progressive loading)
  // Poll market movers while loading (fast polling every 5 seconds)
  useEffect(() => {
    if (!isMarketLiveExpanded || !marketMovers?.loading) return;
    
    const pollInterval = setInterval(() => {
      console.log('Polling market movers for fresh data...');
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market/movers`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.movers) {
            setMarketMovers(data.movers);
            if (!data.movers.loading) {
              console.log('Market movers loading complete, stopping fast poll');
              clearInterval(pollInterval);
            }
          }
        })
        .catch(err => console.error('Error polling movers:', err));
    }, 5000); // Poll every 5 seconds while loading
    
    return () => clearInterval(pollInterval);
  }, [isMarketLiveExpanded, marketMovers?.loading]);

  // Periodic refresh to keep data fresh (every 5 minutes after initial load)
  useEffect(() => {
    if (!isMarketLiveExpanded) return;
    
    // Only start periodic refresh after initial loading is complete
    if (marketMovers?.loading) return;
    
    const refreshInterval = setInterval(() => {
      console.log('Periodic refresh of market movers...');
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market/movers`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.movers) {
            setMarketMovers(data.movers);
          }
        })
        .catch(err => console.error('Error refreshing movers:', err));
    }, 5 * 60 * 1000); // Refresh every 5 minutes
    
    return () => clearInterval(refreshInterval);
  }, [isMarketLiveExpanded, marketMovers?.loading]);

  // Fetch portfolio-specific news separately (depends on stocks being loaded)
  useEffect(() => {
    if (isMarketLiveExpanded && stocks && stocks.length > 0 && portfolioNews.length === 0) {
      const symbols = stocks.map(s => s.symbol).filter(s => s && !s.includes(':'));
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market/portfolio-news`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(symbols)
      })
        .then(res => res.json())
        .then(data => {
          if (data.success && data.news) {
            setPortfolioNews(data.news);
          }
        })
        .catch(err => console.error('Error fetching portfolio news:', err));
    }
  }, [isMarketLiveExpanded, stocks, portfolioNews.length]);

  // Auto-refresh news every 10 minutes when in fullscreen mode
  useEffect(() => {
    if (!isMarketLiveExpanded) return;
    
    const refreshNews = () => {
      console.log('Auto-refreshing Market Live news...');
      
      // Refresh market news
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market/news`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.news) {
            setMarketNews(data.news);
          }
        })
        .catch(err => console.error('Error refreshing news:', err));
      
      // Refresh ticker news
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market/ticker-news`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.news) {
            setTickerNews(data.news);
          }
        })
        .catch(err => console.error('Error refreshing ticker news:', err));
      
      // Refresh market movers
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market/movers`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.movers) {
            setMarketMovers(data.movers);
          }
        })
        .catch(err => console.error('Error refreshing movers:', err));
      
      // Refresh portfolio news if we have stocks
      if (stocks && stocks.length > 0) {
        const symbols = stocks.map(s => s.symbol).filter(s => s && !s.includes(':'));
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market/portfolio-news`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(symbols)
        })
          .then(res => res.json())
          .then(data => {
            if (data.success && data.news) {
              setPortfolioNews(data.news);
            }
          })
          .catch(err => console.error('Error refreshing portfolio news:', err));
      }
    };
    
    // Refresh every 10 minutes (600000ms)
    const intervalId = setInterval(refreshNews, 600000);
    
    return () => clearInterval(intervalId);
  }, [isMarketLiveExpanded, stocks]);

  // Poll API rate limit status when in fullscreen mode
  useEffect(() => {
    if (!isMarketLiveExpanded) {
      setApiRateLimited({ limited: false, seconds_remaining: 0 });
      return;
    }
    
    const checkRateLimit = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market/rate-limit-status`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setApiRateLimited({ limited: data.limited, seconds_remaining: data.seconds_remaining });
          }
        })
        .catch(() => {});
    };
    
    // Check immediately and then every 5 seconds
    checkRateLimit();
    const rateLimitInterval = setInterval(checkRateLimit, 5000);
    
    return () => clearInterval(rateLimitInterval);
  }, [isMarketLiveExpanded]);

  // Fetch profile when profile dialog opens
  useEffect(() => {
    if (isProfileOpen) {
      fetchProfile();
    }
  }, [isProfileOpen]);

  // Privacy: collapse all panels and hide values on login
  const collapseAllOnLogin = () => {
    setHideMarketValue(true);
    setHideTotalValue(true);
    localStorage.setItem('portfolio_hideMarketValue', 'true');
    localStorage.setItem('portfolio_hideTotalValue', 'true');
    setShowMarketPerformance(false);
    setShowMarketLive(false);
    setShowDiversity(false);
    setShowPlatformPerformance(false);
    setShowMoversChart(false);
    setShowStockAnalytics(false);
    setShowStockList(false);
    localStorage.setItem('portfolio_showMarketPerformance', 'false');
    localStorage.setItem('portfolio_showMarketLive', 'false');
    localStorage.setItem('portfolio_showDiversity', 'false');
    localStorage.setItem('portfolio_showPlatformPerformance', 'false');
    localStorage.setItem('portfolio_showMoversChart', 'false');
    localStorage.setItem('portfolio_showStockAnalytics', 'false');
    localStorage.setItem('portfolio_showStockList', 'false');
    localStorage.setItem('nonMarketPanelExpanded', 'false');
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (!email || pin.length < 4) return;
    
    setIsLoggingIn(true);
    setLoginError('');
    setPinError(false);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), pin })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Check if 2FA is required
        if (data.twoFactorRequired) {
          setTwoFactorPending(true);
          setTwoFactorEmail(data.email);
          setTwoFactorData(data);
          setTwoFactorCode('');
          setIsLoggingIn(false);
          return;
        }
        
        // Check if user is admin - redirect to admin panel
        if (data.isAdmin || data.email === 'superadmin@admin.com') {
          sessionStorage.setItem('admin_auth', 'true');
          sessionStorage.setItem('admin_email', data.email);
          navigate('/admin');
          return;
        }
        
        setIsAuthenticated(true);
        sessionStorage.setItem('portfolio_auth', 'true');
        sessionStorage.setItem('portfolio_user_email', data.email);
        
        // Privacy: hide all values and collapse panels on login
        collapseAllOnLogin();
        
        // Try to load cached data for instant display (mobile optimization)
        const cached = loadFromCache(data.email);
        if (cached && cached.stocks && cached.stocks.length > 0) {
          // Show cached data immediately
          setStocks(cached.stocks);
          setPerformance(cached.performance);
          setLastUpdate(new Date(cached.timestamp));
          setIsLoading(false);
          // Fetch fresh data in background (will update cache when done)
        } else {
          // No cache - show loading state
          setStocks([]);
          setPerformance(null);
          setIsLoading(true);
        }
        
        // Store user role (viewer, user, admin)
        const role = data.role || 'user';
        setUserRole(role);
        sessionStorage.setItem('portfolio_user_role', role);
        
        // Remember email if checkbox is checked
        if (rememberEmail) {
          localStorage.setItem('portfolio_email', data.email);
          localStorage.setItem('portfolio_remember_email', 'true');
        } else {
          localStorage.removeItem('portfolio_email');
          localStorage.removeItem('portfolio_remember_email');
          localStorage.removeItem('loginBackground');
        }
        
        setPinError(false);
        setPin('');
        setLoginError('');
        
        // Check if we should show biometric prompt (mobile only, biometric available, not already enabled)
        const existingBiometricData = getBiometricData(data.email);
        const shouldPrompt = 
          isMobileDevice && 
          biometricAvailable && 
          !existingBiometricData?.enabled && 
          !existingBiometricData?.neverAsk;
        
        if (shouldPrompt) {
          // Show prompt after a brief delay so user sees they're logged in
          setTimeout(() => setShowBiometricPrompt(true), 500);
        }
      } else {
        setPinError(true);
        setLoginError(data.message || 'Invalid email or PIN');
        setPin('');
        setTimeout(() => {
          setPinError(false);
          setLoginError('');
        }, 3000);
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Connection error. Please try again.');
      setTimeout(() => setLoginError(''), 3000);
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Handle 2FA code verification
  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFactorCode || twoFactorCode.length < 6) return;
    
    setIsVerifying2FA(true);
    setLoginError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: twoFactorEmail, code: twoFactorCode })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTwoFactorPending(false);
        setTwoFactorCode('');
        
        // Check if user is admin
        if (data.isAdmin || data.email === 'superadmin@admin.com') {
          sessionStorage.setItem('admin_auth', 'true');
          sessionStorage.setItem('admin_email', data.email);
          navigate('/admin');
          return;
        }
        
        setIsAuthenticated(true);
        sessionStorage.setItem('portfolio_auth', 'true');
        sessionStorage.setItem('portfolio_user_email', data.email);
        
        // Privacy: hide all values and collapse panels on login
        collapseAllOnLogin();
        
        const cached = loadFromCache(data.email);
        if (cached && cached.stocks && cached.stocks.length > 0) {
          setStocks(cached.stocks);
          setPerformance(cached.performance);
          setLastUpdate(new Date(cached.timestamp));
          setIsLoading(false);
        } else {
          setStocks([]);
          setPerformance(null);
          setIsLoading(true);
        }
        
        const role = data.role || 'user';
        setUserRole(role);
        sessionStorage.setItem('portfolio_user_role', role);
        
        if (rememberEmail) {
          localStorage.setItem('portfolio_email', data.email);
          localStorage.setItem('portfolio_remember_email', 'true');
        }
        
        setPinError(false);
        setPin('');
        setLoginError('');
      } else {
        setLoginError(data.message || 'Invalid verification code');
        setTwoFactorCode('');
        setTimeout(() => setLoginError(''), 5000);
      }
    } catch (error) {
      console.error('2FA verify error:', error);
      setLoginError('Connection error. Please try again.');
      setTimeout(() => setLoginError(''), 3000);
    } finally {
      setIsVerifying2FA(false);
    }
  };

  // Profile functions
  const fetchProfile = async () => {
    try {
      const userEmail = sessionStorage.getItem('portfolio_user_email');
      if (!userEmail) return;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/profile/${encodeURIComponent(userEmail)}`);
      const data = await response.json();
      
      if (data.success) {
        setProfileData(prev => ({
          ...prev,
          displayName: data.profile.displayName || '',
          hideEmailAmounts: data.profile.hideEmailAmounts || false,
          showMarketInfo: data.profile.showMarketInfo !== false, // Default to true
          showNonMarketAssets: data.profile.showNonMarketAssets !== false, // Default to true
          loginBackground: data.profile.loginBackground || 'none',
          twoFactorEnabled: data.profile.twoFactorEnabled || false
        }));
        // Update display name in header
        if (data.profile.displayName) {
          setUserDisplayName(data.profile.displayName);
        }
        // Also update localStorage for instant load next time
        localStorage.setItem('showMarketInfo', (data.profile.showMarketInfo !== false).toString());
        localStorage.setItem('showNonMarketAssets', (data.profile.showNonMarketAssets !== false).toString());
        localStorage.setItem('loginBackground', data.profile.loginBackground || 'none');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleSaveProfile = async () => {
    const userEmail = sessionStorage.getItem('portfolio_user_email');
    if (!userEmail) return;
    
    // Validate new PIN if provided
    if (profileData.newPin) {
      if (!profileData.currentPin) {
        toast({ title: "Error", description: "Please enter your current PIN/password", variant: "destructive" });
        return;
      }
      if (profileData.newPin.length < 4) {
        toast({ title: "Error", description: "New PIN/password must be at least 4 characters", variant: "destructive" });
        return;
      }
      if (profileData.newPin !== profileData.confirmPin) {
        toast({ title: "Error", description: "New PIN/password doesn't match confirmation", variant: "destructive" });
        return;
      }
    }
    
    setIsSavingProfile(true);
    try {
      const updateData = {};
      
      if (profileData.displayName !== undefined) {
        updateData.displayName = profileData.displayName;
      }
      
      // Include email privacy setting
      updateData.hideEmailAmounts = profileData.hideEmailAmounts;
      
      // Include panel visibility settings
      updateData.showMarketInfo = profileData.showMarketInfo;
      updateData.showNonMarketAssets = profileData.showNonMarketAssets;
      
      // Include login background setting
      updateData.loginBackground = profileData.loginBackground;
      
      // Include 2FA setting
      updateData.twoFactorEnabled = profileData.twoFactorEnabled;
      
      if (profileData.newPin && profileData.currentPin) {
        updateData.currentPin = profileData.currentPin;
        updateData.newPin = profileData.newPin;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/profile/${encodeURIComponent(userEmail)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: "Profile updated successfully" });
        // Update display name in header
        if (profileData.displayName) {
          setUserDisplayName(profileData.displayName);
        }
        // Clear password fields
        setProfileData(prev => ({
          ...prev,
          currentPin: '',
          newPin: '',
          confirmPin: ''
        }));
        setIsProfileOpen(false);
      } else {
        toast({ title: "Error", description: data.detail || "Failed to update profile", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update profile", variant: "destructive" });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handlePinChange = (e) => {
    // Allow alphanumeric for admin passwords
    const value = e.target.value.slice(0, 12);
    setPin(value);
    setPinError(false);
    setLoginError('');
  };

  const handleLogout = useCallback((reason = 'manual') => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('portfolio_auth');
    sessionStorage.removeItem('portfolio_user_email');
    sessionStorage.removeItem('portfolio_user_role');
    // Clear portfolio data from memory (but keep localStorage cache for fast next login)
    setStocks([]);
    setPerformance(null);
    setUserRole('user');
    setPin('');
    
    // Security: Collapse all panels and enable privacy mode on logout
    setShowMarketPerformance(false);
    setShowMarketLive(false);
    setShowDiversity(false);
    setShowPlatformPerformance(false);
    setShowStockAnalytics(false);
    setShowMoversChart(false);
    setShowMovers(false);
    setShowStockList(false);
    setPrivacyMode(true);
    
    // Save collapsed states to localStorage
    localStorage.setItem('portfolio_showMarketPerformance', 'false');
    localStorage.setItem('portfolio_showMarketLive', 'false');
    localStorage.setItem('portfolio_showDiversity', 'false');
    localStorage.setItem('portfolio_showPlatformPerformance', 'false');
    localStorage.setItem('portfolio_showStockAnalytics', 'false');
    localStorage.setItem('portfolio_showMoversChart', 'false');
    localStorage.setItem('portfolio_showMovers', 'false');
    localStorage.setItem('portfolio_showStockList', 'false');
    localStorage.setItem('portfolio_privacyMode', 'true');
    
    if (reason === 'inactivity') {
      toast({
        title: "Session Expired",
        description: "You were logged out due to inactivity",
        variant: "default"
      });
    } else {
      toast({
        title: "Logged Out",
        description: "Portfolio locked successfully"
      });
    }
    
    // Clear loginBackground if "Remember my email" is not active
    if (localStorage.getItem('portfolio_remember_email') !== 'true') {
      localStorage.removeItem('loginBackground');
    }
  }, [toast]);

  // Get current user email
  const getUserEmail = () => {
    return sessionStorage.getItem('portfolio_user_email');
  };

  // Cache helpers for faster mobile loading
  const getCacheKey = (email) => `portfolio_cache_${email?.toLowerCase() || 'default'}`;
  
  const saveToCache = (email, data) => {
    try {
      const cacheKey = getCacheKey(email);
      const cacheData = {
        stocks: data.stocks || [],
        performance: data.performance || null,
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (e) {
      console.error('Error saving to cache:', e);
    }
  };

  const loadFromCache = (email) => {
    try {
      const cacheKey = getCacheKey(email);
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;
      
      const data = JSON.parse(cached);
      // Cache is valid for 24 hours
      const cacheAge = Date.now() - (data.timestamp || 0);
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      
      if (cacheAge > maxAge) {
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      return data;
    } catch (e) {
      console.error('Error loading from cache:', e);
      return null;
    }
  };

  const fetchPortfolio = async () => {
    try {
      let data;
      const userEmail = getUserEmail();
      
      // For viewers, fetch aggregate portfolio (all stocks from all users)
      if (isViewer) {
        data = await portfolioAPI.getAggregatePortfolio();
      } else {
        data = await portfolioAPI.getAllStocks(userEmail);
      }
      
      setStocks(data);
      setLastUpdate(new Date());
      setIsLoading(false);
      
      // Clear performance if no stocks, otherwise fetch it
      if (data.length === 0) {
        setPerformance(null);
        // Save empty cache
        if (!isViewer) saveToCache(userEmail, { stocks: [], performance: null });
      }
      // Performance is fetched in parallel via useEffect when stocks.length changes
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      toast({
        title: "Error",
        description: "Failed to fetch portfolio data",
        variant: "destructive"
      });
      setIsLoading(false);
    }
  };

  const fetchPerformance = async () => {
    try {
      let response;
      const userEmail = getUserEmail();
      
      // For viewers, fetch aggregate performance (all users combined)
      if (isViewer) {
        response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/portfolio/aggregate/performance`);
      } else {
        response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/portfolio/performance`, {
          params: userEmail ? { user_email: userEmail } : {}
        });
      }
      
      setPerformance(response.data);
      
      // Save to cache after performance is fetched (only for non-viewers)
      if (!isViewer && stocks.length > 0) {
        saveToCache(userEmail, { stocks, performance: response.data });
      }
    } catch (error) {
      console.error('Error fetching performance:', error);
      setPerformance(null);
    }
  };

  // Export portfolio to Excel
  const handleExportPortfolio = async () => {
    try {
      setIsExporting(true);
      const email = getUserEmail();
      await portfolioAPI.exportPortfolio(email, selectedPlatform);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export portfolio. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };
  
  // Import portfolio from Excel
  const handleImportPortfolio = async (file) => {
    try {
      setIsImporting(true);
      const email = getUserEmail();
      
      // If a platform filter is active, always replace that platform's stocks
      let replace = false;
      if (selectedPlatform) {
        const confirmed = window.confirm(
          `You are viewing "${selectedPlatform}" stocks.\n\nThis will REPLACE all existing ${selectedPlatform} stocks with the imported file.\n\nContinue?`
        );
        if (!confirmed) {
          setIsImporting(false);
          return;
        }
        replace = true;
      }
      
      const result = await portfolioAPI.importPortfolio(file, email, selectedPlatform, replace);
      
      if (result.success) {
        // Refresh the portfolio to show imported stocks
        await fetchPortfolio();
        
        let message = `Successfully imported ${result.imported} stocks.`;
        if (result.deleted > 0) {
          message = `Replaced ${result.deleted} existing ${selectedPlatform} stocks with ${result.imported} imported stocks.`;
        }
        if (result.errors && result.errors.length > 0) {
          message += `\n\nSome rows had errors:\n${result.errors.join('\n')}`;
        }
        alert(message);
      }
    } catch (error) {
      console.error('Import failed:', error);
      alert('Failed to import portfolio. Please check your Excel file format.\n\nExpected columns: Symbol, Platform, Shares, Cost Basis, Current Price (Optional)');
    } finally {
      setIsImporting(false);
    }
  };

  const formatCurrency = (value) => {
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const calculateMetrics = (stock) => {
    const isCash = stock.symbol?.toUpperCase() === 'CASH';
    const isMunicipal = stock.investmentType === 'MUNICIPAL';
    
    if (isCash) {
      // CASH: value = shares, no gain/loss
      return {
        totalValue: stock.shares.toFixed(2),
        totalCost: stock.shares.toFixed(2),
        gainLoss: '0.00',
        gainLossPercent: '0.00'
      };
    }
    
    // For MUNICIPAL, value is typically stored in currentPrice (par value) or shares
    // Since we don't have accurate cost basis for municipals, show 0% gain/loss
    const totalValue = isMunicipal 
      ? (stock.currentPrice && stock.currentPrice > 1 ? stock.currentPrice : stock.shares)
      : stock.currentPrice * stock.shares;
    const totalCost = isMunicipal 
      ? totalValue // Use current value as cost for municipals (shows 0% gain/loss)
      : stock.costBasis * stock.shares;
    const gainLoss = totalValue - totalCost;
    const gainLossPercent = totalCost > 0 ? ((gainLoss / totalCost) * 100).toFixed(2) : '0.00';
    
    return {
      totalValue: totalValue.toFixed(2),
      totalCost: totalCost.toFixed(2),
      gainLoss: gainLoss.toFixed(2),
      gainLossPercent
    };
  };

  // Investment types that count towards market performance
  // MUNICIPAL bonds excluded - prices don't change frequently, manually updated
  const MARKET_TYPES = ['STOCK', 'BOND', 'BULLION', 'CRYPTO'];
  const NON_MARKET_TYPES = ['CASH', 'MUNICIPAL', 'JEWELRY', 'REAL ESTATE', 'VEHICLES', 'PRIVATE INVESTMENTS', 'FIREARMS', 'ELECTRONICS', 'ART'];
  const isMarketAsset = (stock) => {
    const type = stock.investmentType || 'STOCK';
    // Always treat symbol 'CASH' as non-market, regardless of investmentType setting
    if (stock.symbol?.toUpperCase() === 'CASH') return false;
    // Check if it's a non-market investment type
    if (NON_MARKET_TYPES.includes(type)) return false;
    return MARKET_TYPES.includes(type);
  };

  const portfolioSummary = () => {
    let totalValue = 0;
    let marketOnlyValue = 0;  // Only market assets
    let nonMarketValue = 0;   // Only non-market assets
    let investedValue = 0;
    let investedCost = 0;
    let nonMarketCount = 0;
    
    stocks.forEach(stock => {
      let stockValue;
      
      if (isMarketAsset(stock)) {
        // Market assets: value = shares × currentPrice
        stockValue = stock.currentPrice * stock.shares;
        investedValue += stockValue;
        investedCost += stock.costBasis * stock.shares;
        marketOnlyValue += stockValue;
      } else {
        // Non-market assets: value is stored in currentPrice (shares should be 1)
        // Handle legacy data where value might be in shares (if shares > 1)
        if (stock.shares && stock.shares > 1) {
          stockValue = stock.shares; // Legacy: value was stored in shares
        } else {
          stockValue = stock.currentPrice || 0; // Standard: value in currentPrice
        }
        nonMarketCount += 1;
        // Exclude disposed/sold items from totals
        if (stock.disposed) return;
        nonMarketValue += stockValue;
      }
      
      totalValue += stockValue;
    });
    
    const totalGainLoss = investedValue - investedCost;
    const totalGainLossPercent = investedCost > 0 ? ((totalGainLoss / investedCost) * 100).toFixed(2) : '0.00';
    
    // Display value depends on marketOnlyTotals toggle
    const displayValue = marketOnlyTotals ? marketOnlyValue : totalValue;
    
    return {
      totalValue: totalValue.toFixed(2),        // Always the full total
      marketOnlyValue: marketOnlyValue.toFixed(2), // Market assets only
      nonMarketValue: nonMarketValue.toFixed(2),   // Non-market assets only
      displayValue: displayValue.toFixed(2),    // What to show based on toggle
      totalGainLoss: totalGainLoss.toFixed(2),
      totalGainLossPercent,
      holdings: stocks.length,
      marketHoldings: stocks.length - nonMarketCount,
      nonMarketCount
    };
  };

  const platformSummaries = () => {
    const platforms = {};
    
    // Include market assets AND MUNICIPAL bonds in platform performance
    // MUNICIPAL is technically a market product but was categorized as non-market due to API limitations
    const platformStocks = stocks.filter(stock => isMarketAsset(stock) || stock.investmentType === 'MUNICIPAL');
    
    platformStocks.forEach(stock => {
      const platform = stock.platform || 'Unknown';
      // For MUNICIPAL, value is typically in shares (par value) or currentPrice
      // Since we don't have accurate cost basis for municipals, use value as cost (0% gain/loss)
      const stockValue = stock.investmentType === 'MUNICIPAL' 
        ? (stock.currentPrice && stock.currentPrice > 1 ? stock.currentPrice : stock.shares) 
        : stock.currentPrice * stock.shares;
      const costValue = stock.investmentType === 'MUNICIPAL'
        ? stockValue  // Use current value as cost for municipals (shows 0% gain/loss)
        : stock.costBasis * stock.shares;
      
      if (!platforms[platform]) {
        platforms[platform] = {
          totalValue: 0,
          investedValue: 0,
          investedCost: 0,
          holdings: 0
        };
      }
      
      platforms[platform].totalValue += stockValue;
      platforms[platform].holdings += 1;
      platforms[platform].investedValue += stockValue;
      platforms[platform].investedCost += costValue;
    });
    
    return Object.entries(platforms).map(([name, data]) => {
      const gainLoss = data.investedValue - data.investedCost;
      const gainLossPercent = data.investedCost > 0 ? ((gainLoss / data.investedCost) * 100).toFixed(2) : '0.00';
      return {
        name,
        totalValue: data.totalValue.toFixed(2),
        gainLoss: gainLoss.toFixed(2),
        gainLossPercent,
        holdings: data.holdings
      };
    });
  };

  const handleAddStock = async () => {
    if (!newStock.symbol || !newStock.shares || !newStock.costBasis) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    setIsAddingStock(true);
    
    try {
      const userEmail = getUserEmail();
      const result = await portfolioAPI.addStock(
        newStock.symbol,
        newStock.shares,
        newStock.costBasis,
        newStock.platform || 'Unknown',
        newStock.currentPrice || null,
        userEmail,
        newStock.investmentType || 'STOCK'
      );
      
      await fetchPortfolio();
      
      setNewStock({ symbol: '', shares: '', costBasis: '', platform: '', currentPrice: '', investmentType: 'STOCK' });
      setIsAddDialogOpen(false);
      
      // Check if price needs update (API was rate limited)
      if (result.needsPriceUpdate) {
        toast({
          title: "Stock Added",
          description: `${newStock.symbol.toUpperCase()} added! Price will update automatically when API is available.`,
        });
      } else {
        toast({
          title: "Stock Added",
          description: `${newStock.symbol.toUpperCase()} has been added to your portfolio`
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to add stock. Please verify the symbol is correct.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsAddingStock(false);
    }
  };

  const handleDeleteStock = async (id, symbol) => {
    try {
      const userEmail = getUserEmail();
      await portfolioAPI.deleteStock(id, userEmail);
      
      // Always do a full refresh after delete
      await fetchPortfolio();
      
      toast({
        title: "Stock Removed",
        description: `${symbol} has been removed from your portfolio`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove stock",
        variant: "destructive"
      });
    }
  };

  const handleEditStart = (stock) => {
    setEditingId(stock.id);
    setEditForm({
      shares: stock.shares,
      costBasis: stock.costBasis,
      platform: stock.platform || '',
      investmentType: stock.investmentType || 'STOCK'
    });
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleEditSave = async (stock) => {
    try {
      const userEmail = getUserEmail();
      // Delete old stock and add new one with updated values
      await portfolioAPI.deleteStock(stock.id, userEmail);
      await portfolioAPI.addStock(
        stock.symbol,
        editForm.shares,
        editForm.costBasis,
        editForm.platform || 'Unknown',
        null,
        userEmail,
        editForm.investmentType || stock.investmentType || 'STOCK'
      );
      
      await fetchPortfolio();
      setEditingId(null);
      setEditForm({});
      
      toast({
        title: "Stock Updated",
        description: `${stock.symbol} has been updated`
      });
    } catch (error) {
      console.error('Edit save error:', error);
      toast({
        title: "Error",
        description: "Failed to update stock",
        variant: "destructive"
      });
    }
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    await fetchPortfolio();
  };

  const handleRefreshSingleStock = async (stockId, symbol) => {
    setRefreshingStockId(stockId);
    const userEmail = sessionStorage.getItem('portfolio_user_email');
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/portfolio/${stockId}/refresh?user_email=${encodeURIComponent(userEmail)}`,
        { method: 'POST' }
      );
      
      const data = await response.json();
      
      if (data.success && data.stock) {
        // Update the stock in state
        setStocks(prevStocks => 
          prevStocks.map(s => 
            s.id === stockId ? { ...s, ...data.stock } : s
          )
        );
        setLastUpdate(new Date());
        toast({ 
          title: "Price Updated", 
          description: `${symbol}: $${data.stock.currentPrice?.toFixed(2)}` 
        });
      } else {
        toast({ 
          title: "Error", 
          description: data.detail || `Could not refresh ${symbol}`, 
          variant: "destructive" 
        });
      }
    } catch (error) {
      toast({ 
        title: "Error", 
        description: `Failed to refresh ${symbol}`, 
        variant: "destructive" 
      });
    } finally {
      setRefreshingStockId(null);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Platform sorting handler
  const handlePlatformSort = (column) => {
    if (platformSortColumn === column) {
      setPlatformSortDirection(platformSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setPlatformSortColumn(column);
      setPlatformSortDirection('asc');
    }
  };

  // Get sorted platforms
  const getSortedPlatforms = (platformList) => {
    if (!platformSortColumn) return platformList;
    
    return [...platformList].sort((a, b) => {
      let aValue, bValue;
      
      switch (platformSortColumn) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'holdings':
          aValue = a.holdings;
          bValue = b.holdings;
          break;
        case 'totalValue':
          aValue = parseFloat(a.totalValue);
          bValue = parseFloat(b.totalValue);
          break;
        case 'gainLoss':
          aValue = parseFloat(a.gainLoss);
          bValue = parseFloat(b.gainLoss);
          break;
        case 'gainLossPercent':
          aValue = parseFloat(a.gainLossPercent);
          bValue = parseFloat(b.gainLossPercent);
          break;
        default:
          return 0;
      }
      
      if (platformSortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  // Platform sort icon component
  const PlatformSortIcon = ({ column }) => {
    if (platformSortColumn !== column) {
      return <ArrowUpDown className="w-3 h-3 text-slate-400" />;
    }
    return platformSortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-blue-600" />
      : <ArrowDown className="w-3 h-3 text-blue-600" />;
  };

  // Analytics sorting handler
  const handleAnalyticsSort = (column) => {
    if (analyticsSortColumn === column) {
      setAnalyticsSortDirection(analyticsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setAnalyticsSortColumn(column);
      setAnalyticsSortDirection('asc');
    }
  };

  // Get sorted stocks for analytics
  const getAnalyticsSortedStocks = () => {
    let stockList = selectedPlatform 
      ? stocks.filter(stock => stock.platform === selectedPlatform)
      : stocks;
    
    if (!analyticsSortColumn) return stockList;
    
    return [...stockList].sort((a, b) => {
      let aValue, bValue;
      // Handle MUNICIPAL bonds correctly - value stored in currentPrice or shares
      const aTotal = a.investmentType === 'MUNICIPAL' 
        ? (a.currentPrice && a.currentPrice > 1 ? a.currentPrice : a.shares)
        : a.currentPrice * a.shares;
      const bTotal = b.investmentType === 'MUNICIPAL' 
        ? (b.currentPrice && b.currentPrice > 1 ? b.currentPrice : b.shares)
        : b.currentPrice * b.shares;
      const aCost = a.investmentType === 'MUNICIPAL' ? aTotal : a.costBasis * a.shares;
      const bCost = b.investmentType === 'MUNICIPAL' ? bTotal : b.costBasis * b.shares;
      
      switch (analyticsSortColumn) {
        case 'symbol':
          aValue = a.symbol.toLowerCase();
          bValue = b.symbol.toLowerCase();
          break;
        case 'name':
          aValue = (a.name || a.symbol).toLowerCase();
          bValue = (b.name || b.symbol).toLowerCase();
          break;
        case 'current':
          aValue = a.currentPrice;
          bValue = b.currentPrice;
          break;
        case 'costBasis':
          aValue = a.costBasis;
          bValue = b.costBasis;
          break;
        case 'week52High':
          aValue = a.week52High || a.currentPrice;
          bValue = b.week52High || b.currentPrice;
          break;
        case 'percentFromHigh':
          const aHigh = a.week52High || a.currentPrice;
          const bHigh = b.week52High || b.currentPrice;
          aValue = aHigh > 0 ? ((a.currentPrice - aHigh) / aHigh) * 100 : 0;
          bValue = bHigh > 0 ? ((b.currentPrice - bHigh) / bHigh) * 100 : 0;
          break;
        case 'percentGain':
          aValue = aCost > 0 ? ((aTotal - aCost) / aCost) * 100 : 0;
          bValue = bCost > 0 ? ((bTotal - bCost) / bCost) * 100 : 0;
          break;
        case 'weight':
          // Calculate total portfolio value, handling MUNICIPAL bonds correctly
          const totalPortfolio = stocks.reduce((sum, s) => {
            const stockVal = s.investmentType === 'MUNICIPAL' 
              ? (s.currentPrice && s.currentPrice > 1 ? s.currentPrice : s.shares)
              : s.currentPrice * s.shares;
            return sum + stockVal;
          }, 0);
          aValue = totalPortfolio > 0 ? (aTotal / totalPortfolio) * 100 : 0;
          bValue = totalPortfolio > 0 ? (bTotal / totalPortfolio) * 100 : 0;
          break;
        case 'volume':
          aValue = a.volume || 0;
          bValue = b.volume || 0;
          break;
        default:
          return 0;
      }
      
      if (analyticsSortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  // Analytics sort icon component
  const AnalyticsSortIcon = ({ column }) => {
    if (analyticsSortColumn !== column) {
      return <ArrowUpDown className="w-3 h-3 text-slate-400" />;
    }
    return analyticsSortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-blue-600" />
      : <ArrowDown className="w-3 h-3 text-blue-600" />;
  };

  // Movers sorting handler
  const handleMoversSort = (column) => {
    if (moversSortColumn === column) {
      setMoversSortDirection(moversSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setMoversSortColumn(column);
      setMoversSortDirection('desc'); // Default to desc for price changes (biggest movers first)
    }
  };

  // Calculate price change metrics for a stock
  const getStockPriceChanges = (stock) => {
    const costBasis = stock.costBasis || stock.currentPrice;
    const currentPrice = stock.currentPrice;
    
    // Calculate changes based on cost basis as proxy for historical prices
    // In a real app, you'd fetch historical price data
    const change1D = currentPrice - costBasis * 0.995; // Simulated 1D change
    const change1DPercent = ((currentPrice / (costBasis * 0.995)) - 1) * 100;
    
    const change1W = currentPrice - costBasis * 0.98;
    const change1WPercent = ((currentPrice / (costBasis * 0.98)) - 1) * 100;
    
    const change1M = currentPrice - costBasis * 0.95;
    const change1MPercent = ((currentPrice / (costBasis * 0.95)) - 1) * 100;
    
    // Use actual gain/loss for total change
    const totalChange = currentPrice - costBasis;
    const totalChangePercent = costBasis > 0 ? ((currentPrice - costBasis) / costBasis) * 100 : 0;
    
    return {
      change1D: totalChange * 0.02, // Approximate daily as 2% of total
      change1DPercent: totalChangePercent * 0.02,
      change1W: totalChange * 0.1, // Approximate weekly as 10% of total
      change1WPercent: totalChangePercent * 0.1,
      change1M: totalChange * 0.3, // Approximate monthly as 30% of total
      change1MPercent: totalChangePercent * 0.3,
      totalChange,
      totalChangePercent
    };
  };

  // Get sorted stocks for movers
  const getMoversSortedStocks = () => {
    let stockList = selectedPlatform 
      ? stocks.filter(stock => stock.platform === selectedPlatform)
      : stocks;
    
    return [...stockList].sort((a, b) => {
      const aChanges = getStockPriceChanges(a);
      const bChanges = getStockPriceChanges(b);
      let aValue, bValue;
      
      switch (moversSortColumn) {
        case 'symbol':
          aValue = a.symbol.toLowerCase();
          bValue = b.symbol.toLowerCase();
          break;
        case 'current':
          aValue = a.currentPrice;
          bValue = b.currentPrice;
          break;
        case 'change1D':
          aValue = Math.abs(aChanges.change1DPercent);
          bValue = Math.abs(bChanges.change1DPercent);
          break;
        case 'change1W':
          aValue = Math.abs(aChanges.change1WPercent);
          bValue = Math.abs(bChanges.change1WPercent);
          break;
        case 'change1M':
          aValue = Math.abs(aChanges.change1MPercent);
          bValue = Math.abs(bChanges.change1MPercent);
          break;
        case 'totalChange':
          aValue = Math.abs(aChanges.totalChangePercent);
          bValue = Math.abs(bChanges.totalChangePercent);
          break;
        default:
          return 0;
      }
      
      if (moversSortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  // Movers sort icon component
  const MoversSortIcon = ({ column }) => {
    if (moversSortColumn !== column) {
      return <ArrowUpDown className="w-3 h-3 text-slate-400" />;
    }
    return moversSortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 text-orange-600" />
      : <ArrowDown className="w-3 h-3 text-orange-600" />;
  };

  const getSortedStocks = () => {
    // Filter out non-market assets (CASH, VEHICLES, REAL ESTATE, etc.) - they have their own panel
    // Include MUNICIPAL since it's a market product (just categorized as non-market due to API limitations)
    let filteredStocks = stocks.filter(stock => isMarketAsset(stock) || stock.investmentType === 'MUNICIPAL');
    
    // Filter by selected platform if one is selected
    if (selectedPlatform) {
      filteredStocks = filteredStocks.filter(stock => stock.platform === selectedPlatform);
    }
    
    if (!sortColumn) return filteredStocks;

    return [...filteredStocks].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'symbol':
          aValue = a.symbol;
          bValue = b.symbol;
          break;
        case 'platform':
          // Special handling: sort by platform, then by symbol within platform
          const platformCompare = (a.platform || '').localeCompare(b.platform || '');
          if (platformCompare !== 0) {
            return sortDirection === 'asc' ? platformCompare : -platformCompare;
          }
          // Within same platform, sort by symbol alphabetically
          return a.symbol.localeCompare(b.symbol);
        case 'investmentType':
          // Sort by investment type, then by symbol within same type
          const typeCompare = (a.investmentType || 'STOCK').localeCompare(b.investmentType || 'STOCK');
          if (typeCompare !== 0) {
            return sortDirection === 'asc' ? typeCompare : -typeCompare;
          }
          return a.symbol.localeCompare(b.symbol);
        case 'totalValue':
          // Handle MUNICIPAL bonds correctly - value stored in currentPrice or shares
          aValue = a.investmentType === 'MUNICIPAL' 
            ? (a.currentPrice && a.currentPrice > 1 ? a.currentPrice : a.shares)
            : a.currentPrice * a.shares;
          bValue = b.investmentType === 'MUNICIPAL' 
            ? (b.currentPrice && b.currentPrice > 1 ? b.currentPrice : b.shares)
            : b.currentPrice * b.shares;
          break;
        case 'gainLoss':
          // Handle MUNICIPAL bonds correctly - they show 0% gain/loss
          const aTotal = a.investmentType === 'MUNICIPAL' 
            ? (a.currentPrice && a.currentPrice > 1 ? a.currentPrice : a.shares)
            : a.currentPrice * a.shares;
          const bTotal = b.investmentType === 'MUNICIPAL' 
            ? (b.currentPrice && b.currentPrice > 1 ? b.currentPrice : b.shares)
            : b.currentPrice * b.shares;
          const aCost = a.investmentType === 'MUNICIPAL' ? aTotal : a.costBasis * a.shares;
          const bCost = b.investmentType === 'MUNICIPAL' ? bTotal : b.costBasis * b.shares;
          aValue = aTotal - aCost;
          bValue = bTotal - bCost;
          break;
        default:
          return 0;
      }

      // For non-platform and non-investmentType columns, use standard sorting
      if (sortColumn !== 'platform' && sortColumn !== 'investmentType') {
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (sortDirection === 'asc') {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      }

      return 0;
    });
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-4 h-4 text-slate-400" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="w-4 h-4 text-slate-900" />
    ) : (
      <ArrowDown className="w-4 h-4 text-slate-900" />
    );
  };

  // PIN Entry Screen
  if (!isAuthenticated) {
    const rememberActive = localStorage.getItem('portfolio_remember_email') === 'true';
    const loginBg = rememberActive ? (localStorage.getItem('loginBackground') || 'none') : 'none';
    
    const handleRequestAccess = async (e) => {
      e.preventDefault();
      if (requestForm.email !== requestForm.confirmEmail) {
        toast({ title: "Error", description: "Email addresses do not match", variant: "destructive" });
        return;
      }
      setIsSubmittingRequest(true);
      try {
        // Check if email already exists
        const checkRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/check-email-exists`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: requestForm.email })
        });
        const checkData = await checkRes.json();
        if (checkData.exists) {
          setEmailExistsPrompt(true);
          setIsSubmittingRequest(false);
          return;
        }

        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/request-access`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fullName: requestForm.fullName,
            email: requestForm.email,
            howFound: requestForm.howFound,
            investments: requestForm.investments
          })
        });
        const data = await res.json();
        if (data.success) {
          setRequestSubmitted(true);
        } else {
          toast({ title: "Error", description: data.message || "Failed to submit request", variant: "destructive" });
        }
      } catch {
        toast({ title: "Error", description: "Failed to submit request", variant: "destructive" });
      } finally {
        setIsSubmittingRequest(false);
      }
    };

    const handlePasswordResetRequest = async () => {
      setIsSubmittingRequest(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/request-password-reset`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fullName: requestForm.fullName, email: requestForm.email })
        });
        const data = await res.json();
        if (data.success) {
          setRequestSubmitted(true);
          setEmailExistsPrompt(false);
        } else {
          toast({ title: "Error", description: data.message || "Failed to submit reset request", variant: "destructive" });
        }
      } catch {
        toast({ title: "Error", description: "Failed to submit reset request", variant: "destructive" });
      } finally {
        setIsSubmittingRequest(false);
      }
    };
    
    const loginBackgroundImages = {
      city: 'https://static.prod-images.emergentagent.com/jobs/b52e5b00-6fa3-4caa-9020-519fa348f15f/images/314ca3fcd64f26790573165590bf59cd9aa3d37605dfaaeb880672140a8c363e.png',
      country: 'https://images.unsplash.com/photo-1758164284993-ccbd4f227e8e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxyb2xsaW5nJTIwaGlsbHMlMjBjb3VudHJ5c2lkZSUyMHN1bnNldCUyMGdvbGRlbiUyMGhvdXIlMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzcyMjAxNTA3fDA&ixlib=rb-4.1.0&q=85'
    };
    const bgUrl = loginBackgroundImages[loginBg];
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col relative overflow-hidden">
        {/* Wealth Background - Desktop Only */}
        {bgUrl && (
        <div 
          className="hidden lg:block absolute inset-0 opacity-15"
          style={{
            backgroundImage: `url(${bgUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        )}
        <div className="flex-1 flex items-center justify-center p-4 relative z-10">
        <Card className="w-full max-w-md border-2 border-cyan-400/60 shadow-2xl bg-slate-800/90 backdrop-blur relative"
          style={{
            boxShadow: '0 0 15px rgba(34, 211, 238, 0.4), 0 0 30px rgba(34, 211, 238, 0.2), 0 0 45px rgba(34, 211, 238, 0.1), inset 0 0 15px rgba(34, 211, 238, 0.05)'
          }}>
          <CardHeader className="text-center pb-4 pt-8">
            <div className="flex justify-center mb-4">
              <img 
                src="/logo.png" 
                alt="PortfolioASaurus" 
                className="w-32 h-32 object-contain drop-shadow-lg"
              />
            </div>
            <CardTitle className="text-3xl font-bold text-white mb-2">PortfolioASaurus</CardTitle>
            <p className="text-sm text-slate-400 flex items-center justify-center gap-2">
              <Lock className="w-4 h-4" />
              Secure Access Required
            </p>
          </CardHeader>
          <CardContent className="pb-8">
            {twoFactorPending ? (
              /* 2FA Code Verification Screen */
              <form onSubmit={handleVerify2FA} className="space-y-4">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 mx-auto mb-3 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center justify-center">
                    <Mail className="w-8 h-8 text-emerald-400" />
                  </div>
                  <p className="text-slate-300 text-sm">A 6-digit code was sent to</p>
                  <p className="text-white font-semibold">{twoFactorEmail}</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="twoFactorCode" className="text-slate-300 font-medium text-sm">
                    Verification Code
                  </Label>
                  <Input
                    id="twoFactorCode"
                    type="text"
                    inputMode="numeric"
                    value={twoFactorCode}
                    onChange={(e) => { setTwoFactorCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6)); setLoginError(''); }}
                    placeholder="000000"
                    className="text-center text-3xl tracking-[0.5em] h-16 bg-slate-900/50 border-2 text-white placeholder:text-slate-600 border-slate-600 focus:border-emerald-500 focus:ring-emerald-500/20 font-mono"
                    maxLength={6}
                    autoFocus
                    disabled={isVerifying2FA}
                    data-testid="2fa-code-input"
                  />
                </div>
                
                {loginError && (
                  <div className="flex items-center justify-center gap-2 text-sm text-red-400 bg-red-500/10 py-2 rounded-lg border border-red-500/20">
                    <X className="w-4 h-4" />
                    <span>{loginError}</span>
                  </div>
                )}
                
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white h-12 text-base font-semibold shadow-lg"
                  disabled={twoFactorCode.length < 6 || isVerifying2FA}
                  data-testid="2fa-verify-btn"
                >
                  {isVerifying2FA ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
                  ) : (
                    <><Shield className="w-4 h-4 mr-2" /> Verify Code</>
                  )}
                </Button>
                
                <button 
                  type="button"
                  onClick={() => { setTwoFactorPending(false); setTwoFactorCode(''); setLoginError(''); setPin(''); }}
                  className="w-full text-sm text-slate-400 hover:text-slate-200 py-2 transition-colors"
                  data-testid="2fa-back-btn"
                >
                  Back to login
                </button>
                
                <p className="text-xs text-slate-500 text-center">Code expires in 10 minutes</p>
              </form>
            ) : (
            <form onSubmit={handlePinSubmit} className="space-y-4">
              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300 font-medium text-sm">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setLoginError(''); }}
                  placeholder="Enter your email"
                  className={`h-12 bg-slate-900/50 border-2 text-white placeholder:text-slate-500 ${
                    loginError 
                      ? 'border-red-500 bg-red-500/10' 
                      : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500/20'
                  }`}
                  disabled={isLoggingIn}
                />
              </div>
              
              {/* PIN/Password Field */}
              <div className="space-y-2">
                <Label htmlFor="pin" className="text-slate-300 font-medium text-sm">
                  <div className="flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-emerald-500" />
                    <span>PIN / Password</span>
                  </div>
                </Label>
                <div className="relative">
                  <Input
                    id="pin"
                    type={showPassword ? "text" : "password"}
                    value={pin}
                    onChange={handlePinChange}
                    placeholder="••••••••"
                    className={`text-center text-2xl tracking-widest h-14 bg-slate-900/50 border-2 text-white placeholder:text-slate-600 pr-12 ${
                      pinError 
                        ? 'border-red-500 bg-red-500/10 animate-shake' 
                        : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500/20'
                    }`}
                    maxLength={12}
                    disabled={isLoggingIn}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors p-1"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              {/* Remember Email Checkbox */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="rememberEmail"
                  checked={rememberEmail}
                  onChange={(e) => setRememberEmail(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500/20"
                />
                <Label htmlFor="rememberEmail" className="text-sm text-slate-400 cursor-pointer">
                  Remember my email
                </Label>
              </div>
              
              {/* Error Message */}
              {loginError && (
                <div className="flex items-center justify-center gap-2 text-sm text-red-400 bg-red-500/10 py-2 rounded-lg border border-red-500/20">
                  <X className="w-4 h-4" />
                  <span>{loginError}</span>
                </div>
              )}
              
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white h-12 text-base font-semibold shadow-lg"
                disabled={!email || pin.length < 4 || isLoggingIn}
              >
                {isLoggingIn ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Unlock Portfolio
                  </>
                )}
              </Button>
              
              {/* Biometric Login Button - Show on mobile when biometric is enabled */}
              {biometricEnabled && isMobileDevice && (
                <Button 
                  type="button"
                  onClick={handleBiometricLogin}
                  variant="outline"
                  className="w-full h-12 text-base font-semibold border-2 border-slate-600 text-slate-300 hover:bg-slate-700/50 hover:border-emerald-500/50"
                  disabled={isLoggingIn}
                >
                  <span className="text-xl mr-2">👆</span>
                  Sign In with Fingerprint
                </Button>
              )}
            </form>
            )}
            
            {/* Security Info */}
            <div className="mt-6 space-y-3">
              <div className="h-px bg-slate-700"></div>
              <div className="flex items-center justify-between text-xs text-slate-400 px-1">
                <div className="flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-emerald-500" />
                  <span>Session Protected</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-slate-500" />
                  <span className="font-mono">{userIP}</span>
                </div>
              </div>
              <p className="text-xs text-slate-500 text-center">
                Your data is encrypted and secure
              </p>
              {/* Footer inside card */}
              <div className="pt-3 border-t border-slate-700">
                <p className="text-xs text-slate-500 text-center">
                  {footerText}{footerText ? ' ' : ''}<span className="cursor-pointer hover:text-cyan-400 transition-colors" onClick={() => setShowChangelog(true)}>{footerVersion}</span>
                </p>
              </div>
              
              {/* Access & Info Links */}
              <div className="flex items-center justify-center gap-4 mt-3">
                <button
                  type="button"
                  onClick={() => { setShowRequestAccess(true); setRequestSubmitted(false); setEmailExistsPrompt(false); setRequestForm({ fullName: '', email: '', confirmEmail: '', howFound: '', investments: '' }); }}
                  className="text-xs text-cyan-400 hover:text-cyan-300 underline underline-offset-2 transition-colors"
                  data-testid="request-access-link"
                >
                  Request Access Here
                </button>
                <span className="text-slate-600">|</span>
                <a
                  href="/learn-more"
                  className="text-xs text-cyan-400 hover:text-cyan-300 underline underline-offset-2 transition-colors"
                  data-testid="learn-more-link"
                >
                  Want to learn more?
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
        
        {/* Request Access Modal */}
        {showRequestAccess && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md border border-cyan-400/40 bg-slate-800/95 shadow-2xl"
              style={{ boxShadow: '0 0 20px rgba(34, 211, 238, 0.2)' }}>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl text-white">Request Access</CardTitle>
                  <button onClick={() => setShowRequestAccess(false)} className="text-slate-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-slate-400">Fill out the form below and we will get back to you.</p>
              </CardHeader>
              <CardContent>
                {requestSubmitted ? (
                  <div className="text-center py-6">
                    <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-emerald-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">Request Submitted!</h3>
                    <p className="text-sm text-slate-400">We'll review your request and get back to you shortly.</p>
                    <Button onClick={() => setShowRequestAccess(false)} className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white">
                      Close
                    </Button>
                  </div>
                ) : emailExistsPrompt ? (
                  <div className="text-center py-6" data-testid="email-exists-prompt">
                    <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                      <AlertTriangle className="w-8 h-8 text-amber-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">Account Found</h3>
                    <p className="text-sm text-slate-400 mb-1">
                      It looks like <span className="text-cyan-400 font-medium">{requestForm.email}</span> already has an account.
                    </p>
                    <p className="text-sm text-slate-400 mb-5">Would you like to request a password reset instead?</p>
                    <div className="flex gap-3 justify-center">
                      <Button
                        onClick={handlePasswordResetRequest}
                        disabled={isSubmittingRequest}
                        className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold"
                        data-testid="request-reset-btn"
                      >
                        {isSubmittingRequest ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                        ) : (
                          <><KeyRound className="w-4 h-4 mr-2" /> Reset Password</>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setEmailExistsPrompt(false)}
                        className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                        data-testid="back-to-form-btn"
                      >
                        Go Back
                      </Button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleRequestAccess} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label className="text-slate-300 text-sm">Full Name *</Label>
                      <Input
                        required
                        value={requestForm.fullName}
                        onChange={(e) => setRequestForm(p => ({ ...p, fullName: e.target.value }))}
                        className="bg-slate-900/80 border-slate-600 text-white"
                        placeholder="John Doe"
                        data-testid="request-fullname"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-slate-300 text-sm">Email Address *</Label>
                      <Input
                        required type="email"
                        value={requestForm.email}
                        onChange={(e) => setRequestForm(p => ({ ...p, email: e.target.value }))}
                        className="bg-slate-900/80 border-slate-600 text-white"
                        placeholder="you@email.com"
                        data-testid="request-email"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-slate-300 text-sm">Confirm Email *</Label>
                      <Input
                        required type="email"
                        value={requestForm.confirmEmail}
                        onChange={(e) => setRequestForm(p => ({ ...p, confirmEmail: e.target.value }))}
                        className="bg-slate-900/80 border-slate-600 text-white"
                        placeholder="Confirm your email"
                        data-testid="request-confirm-email"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-slate-300 text-sm">How did you find us?</Label>
                      <Input
                        value={requestForm.howFound}
                        onChange={(e) => setRequestForm(p => ({ ...p, howFound: e.target.value }))}
                        className="bg-slate-900/80 border-slate-600 text-white"
                        placeholder="Google, friend, social media..."
                        data-testid="request-how-found"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-slate-300 text-sm">What investments would you like to track? *</Label>
                      <textarea
                        required
                        value={requestForm.investments}
                        onChange={(e) => setRequestForm(p => ({ ...p, investments: e.target.value }))}
                        className="w-full rounded-md bg-slate-900/80 border border-slate-600 text-white px-3 py-2 text-sm min-h-[80px] focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400/40"
                        placeholder="Stocks, crypto, real estate, vehicles..."
                        data-testid="request-investments"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmittingRequest}
                      className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white h-11 font-semibold"
                      data-testid="request-submit-btn"
                    >
                      {isSubmittingRequest ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                      ) : (
                        <><Mail className="w-4 h-4 mr-2" /> Submit Request</>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        )}
    </div>
    );
  }

  if (isLoading && stocks.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-slate-900 mx-auto mb-4" />
            <p className="text-slate-600">Loading your portfolio...</p>
          </div>
        </div>
        <Footer />
        
        {/* Biometric Setup Prompt - can show while loading */}
        {showBiometricPrompt && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl p-6 max-w-md w-full shadow-2xl border border-slate-700">
              <div className="text-center">
                <div className="text-6xl mb-4">👆</div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Enable Fingerprint Login?
                </h2>
                <p className="text-slate-400 mb-6">
                  Use your fingerprint or Face ID to quickly log in next time without entering your PIN.
                </p>
                
                <div className="space-y-3">
                  <Button 
                    onClick={setupBiometric} 
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-12"
                  >
                    ✓ Enable Fingerprint
                  </Button>
                  <Button 
                    onClick={skipBiometric} 
                    variant="outline" 
                    className="w-full border-slate-600 text-slate-300 hover:bg-slate-700 h-12"
                  >
                    Maybe Later
                  </Button>
                  <button 
                    onClick={neverAskBiometric}
                    className="text-sm text-slate-500 hover:text-slate-400 w-full py-2"
                  >
                    Don&apos;t ask me again
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const summary = portfolioSummary();
  const platforms = platformSummaries();
  const sortedStocks = getSortedStocks();

  return (
    <>
      {/* Emulation Banner - Shows when admin is viewing as another user - ABOVE ENTIRE APP */}
      {emulationInfo && emulationInfo.emulated && (
        <div className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-600 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1">
                <Eye className="w-4 h-4" />
                <span className="text-sm font-medium">Emulation Mode</span>
              </div>
              <span className="text-sm">
                Viewing portfolio as <span className="font-semibold">{emulationInfo.email}</span>
              </span>
            </div>
            <button
              onClick={exitEmulation}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-1.5 rounded-full text-sm font-medium transition-colors"
            >
              <X className="w-4 h-4" />
              Exit & Return to Admin
            </button>
          </div>
        </div>
      )}
      
    <div className={`min-h-screen bg-slate-200 flex flex-col scroll-pt-20 ${mobilePreview ? 'max-w-[375px] mx-auto border-x-4 border-slate-800' : ''} ${emulationInfo?.emulated ? 'pt-10' : ''}`}>
      {/* Biometric Setup Prompt Modal */}
      {showBiometricPrompt && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
          <div className="bg-slate-800 rounded-xl p-6 max-w-md w-full shadow-2xl border border-slate-700">
            <div className="text-center">
              <div className="text-6xl mb-4">👆</div>
              <h2 className="text-2xl font-bold text-white mb-2">
                Enable Fingerprint Login?
              </h2>
              <p className="text-slate-400 mb-6">
                Use your fingerprint or Face ID to quickly log in next time without entering your PIN.
              </p>
              
              <div className="space-y-3">
                <Button 
                  onClick={setupBiometric} 
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-12"
                >
                  ✓ Enable Fingerprint
                </Button>
                <Button 
                  onClick={skipBiometric} 
                  variant="outline" 
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-700 h-12"
                >
                  Maybe Later
                </Button>
                <button 
                  onClick={neverAskBiometric}
                  className="text-sm text-slate-500 hover:text-slate-400 w-full py-2"
                >
                  Don&apos;t ask me again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Stock Ledger Watermark Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {/* Ledger Lines */}
        <svg className="w-full h-full opacity-[0.08]" preserveAspectRatio="none">
          <defs>
            <pattern id="ledgerLines" patternUnits="userSpaceOnUse" width="100%" height="32">
              <line x1="0" y1="31" x2="100%" y2="31" stroke="#64748b" strokeWidth="1"/>
            </pattern>
            <pattern id="verticalLines" patternUnits="userSpaceOnUse" width="120" height="100%">
              <line x1="0" y1="0" x2="0" y2="100%" stroke="#94a3b8" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#ledgerLines)"/>
          <rect width="100%" height="100%" fill="url(#verticalLines)"/>
          {/* Red margin line */}
          <line x1="80" y1="0" x2="80" y2="100%" stroke="#ef4444" strokeWidth="2" opacity="0.5"/>
        </svg>
      </div>
      
      <div className={`relative z-10 mx-auto px-2 sm:px-4 py-2 sm:py-4 flex-1 ${mobilePreview ? 'max-w-[375px]' : 'max-w-7xl w-full'}`}>
        {/* Mobile Header - Compact & Sticky */}
        {/* Mobile Header - Hide when fullscreen is active */}
        <div className={`sm:hidden sticky top-0 z-40 bg-slate-200 -mx-2 px-2 pb-1 pt-1 ${isMarketLiveExpanded ? 'hidden' : ''}`}>
          <div className="flex justify-between items-center">
            {/* Left: Logo & Name */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <img src="/logo.png" alt="PortfolioASaurus" className="w-10 h-10 object-contain" />
              <div>
                <h1 className="font-bold text-slate-900 text-sm">PortfolioASaurus</h1>
                {userDisplayName && (
                  <p className="text-slate-600 text-xs font-medium">Hi, {userDisplayName}</p>
                )}
              </div>
            </div>
            
            {/* Center: Market Status - Only show if market info enabled */}
            {profileData.showMarketInfo && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold ${
              marketStatus.status === 'open' 
                ? 'bg-emerald-100 text-emerald-700' 
                : marketStatus.status === 'premarket'
                ? 'bg-amber-100 text-amber-700'
                : marketStatus.status === 'afterhours'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-slate-100 text-slate-600'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                marketStatus.status === 'open' 
                  ? 'bg-emerald-500 animate-pulse' 
                  : marketStatus.status === 'premarket'
                  ? 'bg-amber-500'
                  : marketStatus.status === 'afterhours'
                  ? 'bg-blue-500'
                  : 'bg-slate-400'
              }`}></span>
              <span>{marketStatus.status === 'open' ? 'Market Open' : marketStatus.status === 'closed' ? 'Market Closed' : marketStatus.label}</span>
            </div>
            )}
            
            {/* Right: Action Buttons */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Hide Market Value Toggle - Mobile - Only show if market info enabled */}
              {profileData.showMarketInfo && (
              <button
                data-testid="hide-market-value-toggle"
                onClick={toggleHideMarketValue}
                className={`p-1.5 rounded-lg transition-all ${
                  hideMarketValue 
                    ? 'bg-amber-500/20 text-amber-600' 
                    : 'bg-slate-100 text-slate-500'
                }`}
                title={hideMarketValue ? 'Show Market Value' : 'Hide Market Value'}
              >
                <DollarSign className="w-4 h-4" />
              </button>
              )}
              {/* Hide Total Value Toggle - Mobile - Only show if market info enabled */}
              {profileData.showMarketInfo && (
              <button
                data-testid="hide-total-value-toggle"
                onClick={toggleHideTotalValue}
                className={`p-1.5 rounded-lg transition-all flex items-center ${
                  hideTotalValue 
                    ? 'bg-teal-500/20 text-teal-600' 
                    : 'bg-slate-100 text-slate-500'
                }`}
                title={hideTotalValue ? 'Show Total Value' : 'Hide Total Value'}
              >
                <span className="text-[10px] font-bold">T</span>
                <DollarSign className="w-3 h-3" />
              </button>
              )}
              {/* Profile Button - Mobile */}
              <button
                onClick={() => setIsProfileOpen(true)}
                className="p-1.5 rounded-lg bg-slate-100 text-slate-500"
              >
                <User className="w-4 h-4" />
              </button>
              {/* Logout Button - Mobile (Far Right) - Hidden during emulation */}
              {!emulationInfo?.emulated && (
                <button
                  onClick={handleLogout}
                  className="p-1.5 rounded-lg bg-red-100 text-red-500"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          
          {/* Mobile Quick Stats Bar - Show market stats only if market info enabled */}
          {profileData.showMarketInfo ? (
          <div className="mt-2 bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-3 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] text-slate-400 uppercase tracking-wider">
                  Total Value
                </div>
                <div className="text-xl font-bold">
                  {hideTotalValue ? '$•••••' : `$${parseFloat(summary.totalValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </div>
              </div>
              <div className="text-center">
                <div className="text-[10px] text-slate-400 uppercase tracking-wider">Market Value</div>
                <div className="text-lg font-bold">
                  {hideMarketValue ? '$•••••' : `$${parseFloat(summary.marketOnlyValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] text-slate-400 uppercase tracking-wider">All Time</div>
                <div className={`text-lg font-bold ${parseFloat(summary.totalGainLoss) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {parseFloat(summary.totalGainLossPercent) >= 0 ? '+' : ''}{summary.totalGainLossPercent}%
                </div>
              </div>
            </div>
          </div>
          ) : (
          /* Simplified header when market info is hidden - only show non-market value */
          <div className="mt-2 bg-gradient-to-r from-amber-700 to-orange-600 rounded-xl p-3 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] text-amber-200 uppercase tracking-wider">
                  Non-Market Assets
                </div>
                <div className="text-xl font-bold">
                  ${parseFloat(summary.nonMarketValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] text-amber-200 uppercase tracking-wider">Holdings</div>
                <div className="text-lg font-bold">
                  {summary.nonMarketCount || 0} items
                </div>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Desktop Header */}
        <div className="hidden sm:block mb-3 sm:mb-4">
          <div className="flex justify-between items-center mb-2 sm:mb-3">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="PortfolioASaurus" className={`${mobilePreview ? 'w-10 h-10' : 'w-14 h-14'} object-contain`} />
              <div className="flex items-baseline gap-2 flex-wrap">
                <h1 className={`font-bold text-slate-900 ${mobilePreview ? 'text-lg' : 'text-xl sm:text-2xl'}`}>PortfolioASaurus</h1>
                {userDisplayName && (
                  <span className={`text-slate-600 font-medium ${mobilePreview ? 'text-sm' : 'text-base sm:text-lg'}`}>
                    — Welcome, {userDisplayName}
                  </span>
                )}
              </div>
            </div>
            <div className={`flex items-center ${mobilePreview ? 'gap-1' : 'gap-2 sm:gap-3'}`}>
              {!mobilePreview && profileData.showMarketInfo && (
                <div className="flex items-center gap-3">
                  {/* Market Status Indicator */}
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${
                    marketStatus.status === 'open' 
                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' 
                      : marketStatus.status === 'premarket'
                      ? 'bg-amber-100 text-amber-700 border border-amber-200'
                      : marketStatus.status === 'afterhours'
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'bg-slate-100 text-slate-600 border border-slate-200'
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${
                      marketStatus.status === 'open' 
                        ? 'bg-emerald-500 animate-pulse' 
                        : marketStatus.status === 'premarket'
                        ? 'bg-amber-500'
                        : marketStatus.status === 'afterhours'
                        ? 'bg-blue-500'
                        : 'bg-slate-400'
                    }`}></span>
                    <span>{marketStatus.label}</span>
                  </div>
                  <p className="text-xs text-slate-500 hidden sm:block">Updated: {lastUpdate.toLocaleTimeString()}</p>
                </div>
              )}
              
              {/* Profile Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsProfileOpen(true)}
                className={`text-slate-600 hover:text-blue-600 hover:border-blue-600 ${mobilePreview ? 'h-7 w-7 p-0' : 'h-8'}`}
                title="Profile Settings"
              >
                <User className={`w-3 h-3 ${mobilePreview ? '' : 'sm:mr-1'}`} />
                {!mobilePreview && <span className="hidden sm:inline">Profile</span>}
              </Button>
              
              {/* Log Out Button - Hidden during emulation */}
              {!emulationInfo?.emulated && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className={`text-slate-600 hover:text-red-600 hover:border-red-600 ${mobilePreview ? 'h-7 w-7 p-0' : 'h-8'}`}
                >
                  <LogOut className={`w-3 h-3 ${mobilePreview ? '' : 'sm:mr-1'}`} />
                  {!mobilePreview && <span className="hidden sm:inline">Log Out</span>}
                </Button>
              )}
            </div>
          </div>

          {/* Profile Dialog */}
          <Dialog open={isProfileOpen} onOpenChange={setIsProfileOpen}>
            <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
              <DialogHeader className="flex-shrink-0">
                <DialogTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" /> Profile Settings
                </DialogTitle>
                <DialogDescription>
                  Update your display name and password
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-2">
                {/* Email (read-only) */}
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input 
                    value={sessionStorage.getItem('portfolio_user_email') || ''} 
                    disabled 
                    className="bg-slate-100"
                  />
                </div>
                
                {/* Display Name */}
                <div className="space-y-2">
                  <Label htmlFor="displayName">Display Name</Label>
                  <Input
                    id="displayName"
                    value={profileData.displayName}
                    onChange={(e) => setProfileData(prev => ({ ...prev, displayName: e.target.value }))}
                    placeholder="Enter your display name"
                  />
                </div>
                
                {/* Change Password Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <KeyRound className="w-4 h-4" /> Change PIN/Password
                  </h4>
                  
                  {/* Current PIN */}
                  <div className="space-y-2 mb-3">
                    <Label htmlFor="currentPin">Current PIN/Password</Label>
                    <div className="relative">
                      <Input
                        id="currentPin"
                        type={showCurrentPin ? "text" : "password"}
                        value={profileData.currentPin}
                        onChange={(e) => setProfileData(prev => ({ ...prev, currentPin: e.target.value }))}
                        placeholder="Enter current PIN/password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPin(!showCurrentPin)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showCurrentPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {/* New PIN */}
                  <div className="space-y-2 mb-3">
                    <Label htmlFor="newPin">New PIN/Password</Label>
                    <div className="relative">
                      <Input
                        id="newPin"
                        type={showNewPin ? "text" : "password"}
                        value={profileData.newPin}
                        onChange={(e) => setProfileData(prev => ({ ...prev, newPin: e.target.value }))}
                        placeholder="Enter new PIN/password (min 4 characters)"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPin(!showNewPin)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showNewPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {/* Confirm New PIN */}
                  <div className="space-y-2">
                    <Label htmlFor="confirmPin">Confirm New PIN/Password</Label>
                    <Input
                      id="confirmPin"
                      type={showNewPin ? "text" : "password"}
                      value={profileData.confirmPin}
                      onChange={(e) => setProfileData(prev => ({ ...prev, confirmPin: e.target.value }))}
                      placeholder="Confirm new PIN/password"
                    />
                    {profileData.newPin && profileData.confirmPin && profileData.newPin !== profileData.confirmPin && (
                      <p className="text-xs text-red-500">Passwords do not match</p>
                    )}
                  </div>
                </div>
                
                {/* Security Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4" /> Security
                  </h4>
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 pr-4">
                        <p className="font-medium text-slate-800 flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-600" /> Auto-Logout Timer
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Automatically log out after this many minutes of inactivity to protect your portfolio.
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={autoLogoutMinutes}
                          onChange={(e) => updateAutoLogoutMinutes(e.target.value)}
                          className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          data-testid="auto-logout-select"
                        >
                          <option value={1}>1 min</option>
                          <option value={5}>5 min</option>
                          <option value={10}>10 min</option>
                          <option value={15}>15 min</option>
                          <option value={25}>25 min</option>
                          <option value={30}>30 min</option>
                          <option value={45}>45 min</option>
                          <option value={60}>60 min</option>
                        </select>
                      </div>
                    </div>
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <Shield className="w-3 h-3" />
                      Warning shows {autoLogoutMinutes <= 1 ? '15 seconds' : autoLogoutMinutes <= 5 ? '30 seconds' : '1 minute'} before auto-logout
                    </p>
                  </div>
                </div>
                
                {/* 2FA Email Verification */}
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 mt-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 pr-4">
                      <p className="font-medium text-slate-800 flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-600" /> Two-Factor Authentication
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Require a verification code sent to your email each time you log in.
                      </p>
                    </div>
                    <Switch
                      checked={profileData.twoFactorEnabled || false}
                      onCheckedChange={(checked) => setProfileData({ ...profileData, twoFactorEnabled: checked })}
                      data-testid="2fa-toggle"
                    />
                  </div>
                </div>
                
                {/* Email Privacy Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email Privacy
                  </h4>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex-1 pr-4">
                      <p className="font-medium text-slate-800">Hide Dollar Amounts in Emails</p>
                      <p className="text-xs text-slate-500 mt-1">
                        When enabled, your daily summary emails will show •••••• instead of dollar amounts. 
                        Click "View Full Report" in the email to reveal amounts.
                      </p>
                    </div>
                    <Switch
                      checked={profileData.hideEmailAmounts}
                      onCheckedChange={(checked) => setProfileData(prev => ({ ...prev, hideEmailAmounts: checked }))}
                    />
                  </div>
                </div>
                
                {/* Dashboard Display Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <LayoutDashboard className="w-4 h-4" /> Dashboard Display
                  </h4>
                  
                  {/* Show Market Information Toggle */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 mb-3">
                    <div className="flex-1 pr-4">
                      <p className="font-medium text-slate-800">Show Market Information</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Display stocks, ETFs, bonds, and other market-based investments.
                      </p>
                    </div>
                    <Switch
                      checked={profileData.showMarketInfo}
                      onCheckedChange={(checked) => {
                        setProfileData(prev => ({ ...prev, showMarketInfo: checked }));
                        localStorage.setItem('showMarketInfo', checked.toString());
                      }}
                      data-testid="show-market-info-toggle"
                    />
                  </div>
                  
                  {/* Show Non-Market Assets Toggle */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex-1 pr-4">
                      <p className="font-medium text-slate-800">Show Non-Market Assets</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Display real estate, vehicles, jewelry, and other non-market assets.
                      </p>
                    </div>
                    <Switch
                      checked={profileData.showNonMarketAssets}
                      onCheckedChange={(checked) => {
                        setProfileData(prev => ({ ...prev, showNonMarketAssets: checked }));
                        localStorage.setItem('showNonMarketAssets', checked.toString());
                      }}
                      data-testid="show-non-market-toggle"
                    />
                  </div>
                </div>
                
                {/* Login Background Section */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" /> Login Background
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: 'none', label: 'None', preview: null },
                      { value: 'city', label: 'City', preview: 'https://static.prod-images.emergentagent.com/jobs/b52e5b00-6fa3-4caa-9020-519fa348f15f/images/314ca3fcd64f26790573165590bf59cd9aa3d37605dfaaeb880672140a8c363e.png' },
                      { value: 'country', label: 'Country', preview: 'https://images.unsplash.com/photo-1758164284993-ccbd4f227e8e?crop=entropy&cs=srgb&fm=jpg&w=400&q=60' }
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        data-testid={`login-bg-${opt.value}`}
                        onClick={() => {
                          setProfileData(prev => ({ ...prev, loginBackground: opt.value }));
                          localStorage.setItem('loginBackground', opt.value);
                        }}
                        className={`relative rounded-lg border-2 overflow-hidden transition-all ${
                          profileData.loginBackground === opt.value
                            ? 'border-emerald-500 ring-2 ring-emerald-500/30'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="aspect-video bg-slate-900 flex items-center justify-center">
                          {opt.preview ? (
                            <img src={opt.preview} alt={opt.label} className="w-full h-full object-cover opacity-40" />
                          ) : (
                            <span className="text-slate-500 text-xs">No Image</span>
                          )}
                        </div>
                        <div className={`text-center text-xs py-1.5 font-medium ${
                          profileData.loginBackground === opt.value
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-slate-50 text-slate-600'
                        }`}>
                          {opt.label}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Biometric Login Section - Always show if biometric was ever enabled or device supports it */}
                {(isMobileDevice || biometricEnabled || biometricAvailable) && (
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <span className="text-lg">👆</span> Fingerprint / Face ID Login
                    </h4>
                    
                    {biometricEnabled ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-emerald-600" />
                            </div>
                            <div>
                              <p className="font-medium text-emerald-800">Biometric Login Enabled</p>
                              <p className="text-xs text-emerald-600">You can log in using fingerprint or Face ID</p>
                            </div>
                          </div>
                        </div>
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            resetBiometric();
                            setIsProfileOpen(false);
                          }}
                          className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Reset Biometrics
                        </Button>
                      </div>
                    ) : biometricAvailable ? (
                      <div className="space-y-3">
                        <p className="text-sm text-slate-600">
                          Enable fingerprint or Face ID for faster, more secure logins.
                        </p>
                        <Button 
                          onClick={() => {
                            setupBiometric();
                          }}
                          className="w-full bg-emerald-500 hover:bg-emerald-600"
                        >
                          <span className="mr-2">👆</span> Enable Biometric Login
                        </Button>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 italic">
                        Biometric login is not available on this device.
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Save Button - Fixed at bottom */}
              <div className="flex justify-end gap-2 pt-4 border-t mt-2 flex-shrink-0">
                <Button variant="outline" onClick={() => setIsProfileOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveProfile} disabled={isSavingProfile}>
                  {isSavingProfile ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Save Changes</>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Summary Line - Desktop Only (mobile has its own header) - Always visible */}
          <div className={`hidden sm:block bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 text-white rounded-xl shadow-lg ${mobilePreview ? 'py-3 px-3' : 'py-3 px-6'}`}>
            {/* Mobile Layout - Now handled in sticky header above */}
            <div className={mobilePreview ? 'block' : 'hidden'}>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-sm uppercase tracking-wide">Total Portfolio</span>
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded">{summary.holdings} positions</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">{marketOnlyTotals ? 'Market Value' : 'Total Value'}</span>
                  <span className="text-xl font-bold">${formatCurrencyPrivate(summary.displayValue)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-300">Market P&L</span>
                  <div className="flex items-center gap-1">
                    {parseFloat(summary.totalGainLoss) >= 0 ? (
                      <TrendingUp className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-red-400" />
                    )}
                    <span className={`text-sm font-bold ${parseFloat(summary.totalGainLoss) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      ${formatCurrencyPrivate(summary.totalGainLoss)} ({summary.totalGainLossPercent}%)
                    </span>
                  </div>
                </div>
              </div>
              {/* Mobile Performance - Show only ALL */}
              {performance?.total && (
                <div className="mt-3 pt-3 border-t border-white/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-300">All Time Return</span>
                      <span className="text-[9px] text-teal-400 uppercase tracking-wide">(Market Only)</span>
                    </div>
                    <span className={`text-sm font-bold ${parseFloat(performance.total.allTime?.changePercent || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {parseFloat(performance.total.allTime?.changePercent || 0) >= 0 ? '+' : ''}{performance.total.allTime?.changePercent || 0}%
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Desktop Layout */}
            <div className={mobilePreview ? 'hidden' : 'block'}>
              {/* Top Row: Title + Values - All aligned at top */}
              <div className="flex items-start justify-between">
                {/* Left: Title, Privacy Toggle, and Add Button */}
                <div className="flex items-start gap-4">
                  <div>
                    <h1 className="font-bold text-xl uppercase tracking-wide text-white">
                      {isViewer ? 'All Portfolios Combined' : (marketOnlyTotals ? 'Market Portfolio' : 'Total Portfolio')}
                    </h1>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-slate-300">
                        {marketOnlyTotals ? summary.marketHoldings : summary.holdings} {(marketOnlyTotals ? summary.marketHoldings : summary.holdings) === 1 ? 'position' : 'positions'}
                        {marketOnlyTotals && summary.nonMarketCount > 0 && (
                          <span className="text-slate-400 ml-1">
                            (+{summary.nonMarketCount} non-market)
                          </span>
                        )}
                      </span>
                      {isViewer && (
                        <span className="text-[10px] bg-violet-500/30 text-violet-300 px-2 py-0.5 rounded-full font-semibold">
                          VIEWER MODE
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Hide Market Value Toggle Button ($ with eye) */}
                  <Button
                    data-testid="desktop-hide-market-toggle"
                    variant="ghost"
                    size="sm"
                    onClick={toggleHideMarketValue}
                    className={`h-9 px-3 rounded-lg transition-all ${
                      hideMarketValue 
                        ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30' 
                        : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                    }`}
                    title={hideMarketValue ? 'Show Market Value' : 'Hide Market Value'}
                  >
                    <DollarSign className={`w-4 h-4 ${hideMarketValue ? 'line-through' : ''}`} />
                    {hideMarketValue && <EyeOff className="w-3 h-3 ml-1" />}
                  </Button>
                  {/* Hide Total Value Toggle Button (T$) */}
                  <Button
                    data-testid="desktop-hide-total-toggle"
                    variant="ghost"
                    size="sm"
                    onClick={toggleHideTotalValue}
                    className={`h-9 px-3 rounded-lg transition-all ${
                      hideTotalValue 
                        ? 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30' 
                        : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                    }`}
                    title={hideTotalValue ? 'Show Total Value' : 'Hide Total Value'}
                  >
                    <span className="text-xs font-bold mr-1">T</span>
                    <DollarSign className={`w-4 h-4 ${hideTotalValue ? 'line-through' : ''}`} />
                    {hideTotalValue && <TrendingUp className="w-3 h-3 ml-1" />}
                  </Button>
                  {!isViewer && (
                  <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 h-9 px-4">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Stock
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New Stock</DialogTitle>
                        <DialogDescription>
                          Add a stock to your portfolio. We will fetch the current price automatically.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label htmlFor="symbol">Stock Symbol</Label>
                          <Input
                            id="symbol"
                            placeholder="e.g., AAPL"
                            value={newStock.symbol}
                            onChange={(e) => setNewStock({ ...newStock, symbol: e.target.value })}
                            className="uppercase"
                            disabled={isAddingStock}
                          />
                        </div>
                        <div>
                          <Label htmlFor="platform">Platform</Label>
                          <Input
                            id="platform"
                            placeholder="e.g., Robinhood, Fidelity, E-Trade"
                            value={newStock.platform}
                            onChange={(e) => setNewStock({ ...newStock, platform: e.target.value })}
                            disabled={isAddingStock}
                          />
                        </div>
                        <div>
                          <Label htmlFor="shares">Number of Shares</Label>
                          <Input
                            id="shares"
                            type="number"
                            placeholder="e.g., 10"
                            value={newStock.shares}
                            onChange={(e) => setNewStock({ ...newStock, shares: e.target.value })}
                            disabled={isAddingStock}
                          />
                        </div>
                        <div>
                          <Label htmlFor="costBasis">Cost Basis (per share)</Label>
                          <Input
                            id="costBasis"
                            type="number"
                            step="0.01"
                            placeholder="e.g., 150.00"
                            value={newStock.costBasis}
                            onChange={(e) => setNewStock({ ...newStock, costBasis: e.target.value })}
                            disabled={isAddingStock}
                          />
                        </div>
                        <div>
                          <Label htmlFor="currentPrice">Current Price (optional - leave blank to auto-fetch)</Label>
                          <Input
                            id="currentPrice"
                            type="number"
                            step="0.01"
                            placeholder="Auto-fetch if blank"
                            value={newStock.currentPrice}
                            onChange={(e) => setNewStock({ ...newStock, currentPrice: e.target.value })}
                            disabled={isAddingStock}
                          />
                        </div>
                        <div>
                          <Label htmlFor="investmentType">Investment Type</Label>
                          <select
                            id="investmentType"
                            value={newStock.investmentType}
                            onChange={(e) => setNewStock({ ...newStock, investmentType: e.target.value })}
                            disabled={isAddingStock}
                            className="w-full h-10 px-3 py-2 text-sm border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                          >
                            <optgroup label="Market Assets (count in performance)">
                              <option value="STOCK">Stock</option>
                              <option value="BOND">Bond</option>
                              <option value="MUNICIPAL">Municipal Bond</option>
                              <option value="BULLION">Bullion</option>
                              <option value="CRYPTO">Crypto</option>
                            </optgroup>
                            <optgroup label="Non-Market Assets (value only)">
                              <option value="ART">Art</option>
                              <option value="CASH">Cash</option>
                              <option value="ELECTRONICS">Electronics/Stereo</option>
                              <option value="FIREARMS">Guns/Weapons</option>
                              <option value="JEWELRY">Jewelry</option>
                              <option value="PRIVATE INVESTMENTS">Private Investments</option>
                              <option value="REAL ESTATE">Real Estate</option>
                              <option value="VEHICLES">Vehicles</option>
                            </optgroup>
                          </select>
                          <p className="text-xs text-slate-500 mt-1">
                            Non-market assets are included in total value but not performance percentages.
                          </p>
                        </div>
                        <Button 
                          onClick={handleAddStock} 
                          className="w-full"
                          disabled={isAddingStock || !newStock.symbol || !newStock.shares || !newStock.costBasis}
                        >
                          {isAddingStock ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Adding...
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-2" />
                              Add Stock
                            </>
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  )}
                </div>
                {/* Right: Market Value and Total Value side by side */}
                <div className="flex items-start gap-6">
                  {/* Market Value with P&L below - hidden when hideMarketValue is true */}
                  <div className="text-right">
                    <div className="text-xs text-slate-400 uppercase tracking-wider font-medium">
                      Market Value
                    </div>
                    <div className="text-2xl font-bold text-white whitespace-nowrap">
                      {hideMarketValue ? '$•••••' : `$${parseFloat(summary.marketOnlyValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    </div>
                    {/* Market P&L below Market Value */}
                    <div className="flex items-center justify-end gap-1.5 mt-0.5">
                      {parseFloat(summary.totalGainLoss) >= 0 ? (
                        <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                      ) : (
                        <TrendingDown className="w-3.5 h-3.5 text-red-400" />
                      )}
                      <span className={`text-base font-semibold ${parseFloat(summary.totalGainLoss) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {hideMarketValue ? '$•••••' : `$${parseFloat(summary.totalGainLoss).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      </span>
                      <span className={`text-xs font-medium ${parseFloat(summary.totalGainLoss) >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                        ({summary.totalGainLossPercent}%)
                      </span>
                    </div>
                  </div>
                  {/* Vertical Divider */}
                  <div className="h-12 w-px bg-white/20"></div>
                  {/* Total Value - hidden when hideMarketValue OR hideTotalValue is true */}
                  <div className="text-right">
                    <div className="text-xs text-slate-400 uppercase tracking-wider font-medium">
                      Total Value
                    </div>
                    <div className="text-2xl font-bold text-white whitespace-nowrap">
                      {hideTotalValue ? '$•••••' : `$${parseFloat(summary.totalValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Performance panel moved to separate collapsible panel below */}
            </div>
          </div>
        </div>

        {/* Market Performance Panel - Collapsible like other panels - Hide when market info disabled */}
        {profileData.showMarketInfo && performance?.total && (
          <div className="hidden sm:block rounded-xl overflow-hidden shadow-lg mb-4">
            {/* Panel Header */}
            <div 
              className="bg-gradient-to-r from-teal-600 to-cyan-600 px-5 py-3 cursor-pointer"
              onClick={() => {
                const newValue = !showMarketPerformance;
                setShowMarketPerformance(newValue);
                localStorage.setItem('portfolio_showMarketPerformance', newValue.toString());
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Market Performance</h2>
                    <p className="text-xs text-white/70">Market returns only • Excludes non-market assets</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => e.stopPropagation()}
                    className="text-white/80 hover:text-white hover:bg-white/10 p-1 h-auto"
                  >
                    {showMarketPerformance ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Panel Content */}
            {showMarketPerformance && (
              <div className="grid grid-cols-5 bg-slate-800">
                {/* 1D */}
                <div className={`py-4 px-4 text-center border-r border-slate-700 hover:bg-slate-700 transition-colors ${performance.total['1D']?.estimated ? 'opacity-60' : ''}`}>
                  <div className="text-slate-400 text-xs uppercase tracking-widest mb-1 font-semibold">
                    {marketStatus.status === 'open' ? '1 Day' : 'Last Close'}
                  </div>
                  <div className="text-white text-lg font-bold">
                    {parseFloat(performance.total['1D']?.change || 0) >= 0 ? '+' : ''}${formatCurrencyMarket(performance.total['1D']?.change || 0)}
                  </div>
                  <div className={`text-sm font-semibold ${parseFloat(performance.total['1D']?.changePercent || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {parseFloat(performance.total['1D']?.changePercent || 0) >= 0 ? '+' : ''}{performance.total['1D']?.changePercent || 0}%
                  </div>
                </div>

                {/* 1W */}
                <div className={`py-4 px-4 text-center border-r border-slate-700 hover:bg-slate-700 transition-colors ${performance.total['1W']?.estimated ? 'opacity-60' : ''}`}>
                  <div className="text-slate-400 text-xs uppercase tracking-widest mb-1 font-semibold">1 Week</div>
                  <div className="text-white text-lg font-bold">
                    {parseFloat(performance.total['1W']?.change || 0) >= 0 ? '+' : ''}${formatCurrencyMarket(performance.total['1W']?.change || 0)}
                  </div>
                  <div className={`text-sm font-semibold ${parseFloat(performance.total['1W']?.changePercent || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {parseFloat(performance.total['1W']?.changePercent || 0) >= 0 ? '+' : ''}{performance.total['1W']?.changePercent || 0}%
                  </div>
                </div>

                {/* 1M */}
                <div className={`py-4 px-4 text-center border-r border-slate-700 hover:bg-slate-700 transition-colors ${performance.total['1M']?.estimated ? 'opacity-60' : ''}`}>
                  <div className="text-slate-400 text-xs uppercase tracking-widest mb-1 font-semibold">1 Month</div>
                  <div className="text-white text-lg font-bold">
                    {parseFloat(performance.total['1M']?.change || 0) >= 0 ? '+' : ''}${formatCurrencyMarket(performance.total['1M']?.change || 0)}
                  </div>
                  <div className={`text-sm font-semibold ${parseFloat(performance.total['1M']?.changePercent || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {parseFloat(performance.total['1M']?.changePercent || 0) >= 0 ? '+' : ''}{performance.total['1M']?.changePercent || 0}%
                  </div>
                </div>

                {/* 1Y */}
                <div className={`py-4 px-4 text-center border-r border-slate-700 hover:bg-slate-700 transition-colors ${performance.total['1Y']?.estimated ? 'opacity-60' : ''}`}>
                  <div className="text-slate-400 text-xs uppercase tracking-widest mb-1 font-semibold">1 Year</div>
                  <div className="text-white text-lg font-bold">
                    {parseFloat(performance.total['1Y']?.change || 0) >= 0 ? '+' : ''}${formatCurrencyMarket(performance.total['1Y']?.change || 0)}
                  </div>
                  <div className={`text-sm font-semibold ${parseFloat(performance.total['1Y']?.changePercent || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {parseFloat(performance.total['1Y']?.changePercent || 0) >= 0 ? '+' : ''}{performance.total['1Y']?.changePercent || 0}%
                  </div>
                </div>

                {/* ALL - highlighted */}
                <div className="py-4 px-4 text-center bg-emerald-600 hover:bg-emerald-500 transition-colors">
                  <div className="text-white/80 text-xs uppercase tracking-widest mb-1 font-bold">All Time</div>
                  <div className="text-white text-lg font-bold">
                    {parseFloat(performance.total.allTime?.change || 0) >= 0 ? '+' : ''}${formatCurrencyMarket(performance.total.allTime?.change || 0)}
                  </div>
                  <div className="text-green-200 text-sm font-bold">
                    {parseFloat(performance.total.allTime?.changePercent || 0) >= 0 ? '+' : ''}{performance.total.allTime?.changePercent || 0}%
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Render panels in order */}
        {panelOrder.map((panelId, panelIndex) => {
          const isFirst = panelIndex === 0;
          const isLast = panelIndex === panelOrder.length - 1;
          
          // Hide market-related panels if showMarketInfo is false
          const marketPanels = ['marketlive', 'diversity', 'platform', 'analytics', 'leaders', 'stocks', 'etfs', 'bonds', 'reits', 'cash', 'crypto'];
          if (!profileData.showMarketInfo && marketPanels.includes(panelId)) {
            return null;
          }
          
          // Hide non-market assets panel if showNonMarketAssets is false
          if (!profileData.showNonMarketAssets && panelId === 'nonmarket') {
            return null;
          }
          
          // Panel Move Buttons Component (for left side)
          const PanelMoveButtons = ({ id, lightTheme = false }) => (
            <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => movePanelUp(id)}
                disabled={isFirst}
                className={`p-1 h-auto ${isFirst ? 'opacity-30 cursor-not-allowed' : lightTheme ? 'text-white/60 hover:text-white hover:bg-white/20' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'}`}
                title="Move panel up"
              >
                <MoveUp className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => movePanelDown(id)}
                disabled={isLast}
                className={`p-1 h-auto ${isLast ? 'opacity-30 cursor-not-allowed' : lightTheme ? 'text-white/60 hover:text-white hover:bg-white/20' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'}`}
                title="Move panel down"
              >
                <MoveDown className="w-4 h-4" />
              </Button>
            </div>
          );

          // Market Live Panel - Animated Visual Market
          if (panelId === 'marketlive') {
            return (
              <div key="marketlive" className={`${isMarketLiveExpanded ? 'fixed inset-0 z-50 p-0 sm:p-4 bg-slate-900/95 backdrop-blur-sm overflow-hidden' : `${mobilePreview ? 'mb-2' : 'mb-2 sm:mb-4'} scroll-mt-32 sm:scroll-mt-0`}`} style={isMarketLiveExpanded ? { scrollbarWidth: 'none', msOverflowStyle: 'none' } : {}}>
                <div className={`bg-white overflow-hidden ${isMarketLiveExpanded ? 'h-full flex flex-col rounded-none sm:rounded-xl sm:shadow-md sm:border-2 sm:border-slate-200' : 'rounded-xl shadow-md border-2 border-slate-200'}`}>
                  {/* Mobile Header - Clickable to expand/collapse */}
                  <div 
                    className="sm:hidden bg-gradient-to-r from-violet-600 to-fuchsia-600 px-3 py-2 cursor-pointer"
                    onClick={() => {
                      if (isMarketLiveExpanded) {
                        // In fullscreen mode - exit fullscreen and restore previous visibility state
                        setIsMarketLiveExpanded(false);
                        if (!wasMarketLiveShown) {
                          setShowMarketLive(false);
                          localStorage.setItem('portfolio_showMarketLive', 'false');
                        }
                      } else {
                        // Not in fullscreen - toggle visibility
                        toggleMarketLive();
                      }
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <h2 className={`font-bold text-white ${isMarketLiveExpanded ? 'text-sm' : 'text-base'}`}>Market Live</h2>
                          {!isMarketLiveExpanded && <p className="text-[10px] text-white/70">Size = Position Value</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Live indicator - Mobile */}
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-white/20 rounded-full">
                          <span className={`w-1.5 h-1.5 rounded-full ${marketStatus.status === 'open' ? 'bg-emerald-400 animate-pulse' : 'bg-slate-300'}`}></span>
                          <span className="text-[10px] font-semibold text-white">{marketStatus.status === 'open' ? 'LIVE' : 'CLOSED'}</span>
                        </div>
                        {/* Fullscreen toggle - Mobile */}
                        <button
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            const newExpanded = !isMarketLiveExpanded;
                            if (newExpanded) {
                              setWasMarketLiveShown(showMarketLive);
                              if (!showMarketLive) setShowMarketLive(true);
                            } else {
                              if (!wasMarketLiveShown) {
                                setShowMarketLive(false);
                                localStorage.setItem('portfolio_showMarketLive', 'false');
                              }
                            }
                            setIsMarketLiveExpanded(newExpanded);
                          }}
                          className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-lg text-white"
                        >
                          {isMarketLiveExpanded ? (
                            <Minimize2 className="w-4 h-4" />
                          ) : (
                            <Maximize2 className="w-4 h-4" />
                          )}
                        </button>
                        {/* Collapse indicator - Mobile - Hide in fullscreen mode */}
                        {!isMarketLiveExpanded && (
                        <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg text-white">
                          {showMarketLive ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </div>
                        )}
                      </div>
                    </div>
                    {/* Time Period Toggle - Mobile (only show when expanded and NOT fullscreen) */}
                    {showMarketLive && !isMarketLiveExpanded && (
                      <div 
                        className="flex items-center justify-center bg-white/20 rounded-lg p-0.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {['1D', '1W', '1M', '1Y', 'ALL'].map((period) => (
                          <button
                            key={period}
                            onClick={() => setMarketLivePeriod(period)}
                            className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded-md transition-all ${
                              marketLivePeriod === period 
                                ? 'bg-white text-violet-600 shadow-sm' 
                                : 'text-white/80'
                            }`}
                          >
                            {period}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Desktop Header */}
                  <div 
                    className="hidden sm:block bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 sm:px-5 py-3 cursor-pointer relative"
                    onClick={() => {
                      if (isMarketLiveExpanded) {
                        // In fullscreen mode - exit fullscreen and restore previous visibility state
                        setIsMarketLiveExpanded(false);
                        // Restore the panel to its previous collapsed/expanded state
                        if (!wasMarketLiveShown) {
                          setShowMarketLive(false);
                          localStorage.setItem('portfolio_showMarketLive', 'false');
                        }
                      } else {
                        // Not in fullscreen - toggle visibility
                        toggleMarketLive();
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <PanelMoveButtons id="marketlive" lightTheme={true} />
                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                          <TrendingUp className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h2 className="text-lg sm:text-xl font-bold text-white">Market Live</h2>
                          <p className="text-xs text-white/70">Your portfolio visualized • Size = Position Value</p>
                        </div>
                      </div>
                      
                      {/* User Name - Center (only in fullscreen mode) */}
                      {isMarketLiveExpanded && (
                        <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-2">
                          <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                            <User className="w-5 h-5 text-white/80" />
                            <span className="text-lg font-bold text-white">
                              {isViewer ? 'Viewer Mode' : (userDisplayName || 'Portfolio Owner')}
                            </span>
                            {isViewer && (
                              <span className="text-[10px] bg-violet-400/30 text-violet-200 px-2 py-0.5 rounded-full font-semibold">
                                ALL PORTFOLIOS
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2">
                        {/* Privacy Toggle - Only when NOT fullscreen */}
                        {!isMarketLiveExpanded && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); togglePrivacyMode(); }}
                            className={`text-white/80 hover:text-white hover:bg-white/10 p-1.5 h-auto ${
                              privacyMode ? 'bg-amber-500/30 text-amber-300' : ''
                            }`}
                            title={privacyMode ? 'Show dollar amounts' : 'Hide dollar amounts for privacy'}
                          >
                            <DollarSign className={`w-4 h-4 ${privacyMode ? 'line-through' : ''}`} />
                          </Button>
                        )}
                        {/* Time Period Toggle */}
                        <div className="flex items-center bg-white/20 rounded-lg p-0.5 backdrop-blur-sm" onClick={(e) => e.stopPropagation()}>
                          {['1D', '1W', '1M', '1Y', 'ALL'].map((period) => (
                            <button
                              key={period}
                              onClick={() => setMarketLivePeriod(period)}
                              className={`px-2.5 py-1.5 text-xs font-semibold rounded-md transition-all ${
                                marketLivePeriod === period 
                                  ? 'bg-white text-violet-600 shadow-sm' 
                                  : 'text-white/80 hover:text-white hover:bg-white/10'
                              }`}
                            >
                              {period}
                            </button>
                          ))}
                        </div>
                        {/* Live indicator */}
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full backdrop-blur-sm">
                          <span className={`w-2 h-2 rounded-full ${marketStatus.status === 'open' ? 'bg-emerald-400 animate-pulse' : 'bg-slate-300'}`}></span>
                          <span className="text-xs font-semibold text-white">{marketStatus.status === 'open' ? 'LIVE' : 'MARKET CLOSED'}</span>
                        </div>
                        {/* API Status Indicator - Always visible */}
                        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${
                          apiRateLimited.limited 
                            ? 'bg-amber-500/30 border border-amber-400/50 animate-pulse' 
                            : 'bg-emerald-500/20 border border-emerald-400/30'
                        }`}>
                          <div className={`w-2 h-2 rounded-full ${apiRateLimited.limited ? 'bg-amber-400' : 'bg-emerald-400 animate-pulse'}`}></div>
                          <span className={`text-[10px] font-semibold ${apiRateLimited.limited ? 'text-amber-200' : 'text-emerald-200'}`}>
                            {apiRateLimited.limited 
                              ? `API LIMIT (${apiRateLimited.seconds_remaining}s)` 
                              : 'API OK'}
                          </span>
                        </div>
                        {/* Expand/Collapse Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            const newExpanded = !isMarketLiveExpanded;
                            if (newExpanded) {
                              // Expanding: save current state and show content
                              setWasMarketLiveShown(showMarketLive);
                              if (!showMarketLive) {
                                setShowMarketLive(true);
                              }
                            } else {
                              // Collapsing: restore previous state
                              if (!wasMarketLiveShown) {
                                setShowMarketLive(false);
                                localStorage.setItem('portfolio_showMarketLive', 'false');
                              }
                            }
                            setIsMarketLiveExpanded(newExpanded);
                          }}
                          className="text-white/80 hover:text-white hover:bg-white/10 p-1 h-auto"
                          title={isMarketLiveExpanded ? "Exit fullscreen" : "Expand to fullscreen"}
                        >
                          {isMarketLiveExpanded ? (
                            <Minimize2 className="w-5 h-5" />
                          ) : (
                            <Maximize2 className="w-5 h-5" />
                          )}
                        </Button>
                        {/* Collapse/Expand Toggle - Hide when in fullscreen mode */}
                        {!isMarketLiveExpanded && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); toggleMarketLive(); }}
                            className="text-white/80 hover:text-white hover:bg-white/10 p-1 h-auto"
                          >
                            {showMarketLive ? (
                              <ChevronUp className="w-5 h-5" />
                            ) : (
                              <ChevronDown className="w-5 h-5" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Market Live Content */}
                  {showMarketLive && stocks.length > 0 && (
                    <div className={`p-2 sm:p-6 bg-gradient-to-b from-slate-900 to-slate-800 relative ${isMarketLiveExpanded ? 'flex-1 min-h-0 max-sm:overflow-visible overflow-hidden' : 'min-h-[300px] overflow-hidden'}`} style={isMarketLiveExpanded ? { scrollbarWidth: 'none', msOverflowStyle: 'none' } : {}}>
                      {/* Background Market Visualization */}
                      {(() => {
                        // Calculate market breadth (stocks up vs down) for background
                        // Filter out non-market assets from market calculations
                        const marketStocks = stocks.filter(s => isMarketAsset(s));
                        const upStocks = marketStocks.filter(s => (s.dailyChangePercent || 0) > 0).length;
                        const downStocks = marketStocks.filter(s => (s.dailyChangePercent || 0) < 0).length;
                        const totalStocks = marketStocks.length;
                        const upPercent = totalStocks > 0 ? (upStocks / totalStocks) * 100 : 50;
                        const avgChange = totalStocks > 0 ? marketStocks.reduce((sum, s) => sum + (s.dailyChangePercent || 0), 0) / totalStocks : 0;
                        const isMarketUp = avgChange >= 0;
                        
                        return (
                          <>
                            {/* Animated gradient background based on market direction */}
                            <div 
                              className={`absolute inset-0 opacity-20 transition-all duration-1000 ${
                                isMarketUp 
                                  ? 'bg-gradient-to-t from-emerald-900 via-transparent to-transparent' 
                                  : 'bg-gradient-to-b from-red-900 via-transparent to-transparent'
                              }`}
                            />
                            
                            {/* Background line chart pattern */}
                            <svg className="absolute inset-0 w-full h-full opacity-10" preserveAspectRatio="none">
                              <defs>
                                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                  <stop offset="0%" stopColor={isMarketUp ? '#10b981' : '#ef4444'} stopOpacity="0.3" />
                                  <stop offset="50%" stopColor={isMarketUp ? '#10b981' : '#ef4444'} stopOpacity="0.6" />
                                  <stop offset="100%" stopColor={isMarketUp ? '#10b981' : '#ef4444'} stopOpacity="0.3" />
                                </linearGradient>
                              </defs>
                              {/* Generate a pseudo-random wave pattern based on market data */}
                              <path
                                d={`M 0 ${isMarketUp ? 180 : 120} 
                                    Q 80 ${isMarketUp ? 140 : 160}, 160 ${isMarketUp ? 120 : 180}
                                    T 320 ${isMarketUp ? 100 : 200}
                                    T 480 ${isMarketUp ? 80 : 220}
                                    T 640 ${isMarketUp ? 60 : 180}
                                    T 800 ${isMarketUp ? 80 : 160}
                                    T 960 ${isMarketUp ? 100 : 140}
                                    T 1120 ${isMarketUp ? 70 : 170}
                                    T 1280 ${isMarketUp ? 50 : 150}
                                    T 1440 ${isMarketUp ? 80 : 120}
                                    T 1600 ${isMarketUp ? 60 : 100}
                                    T 1920 ${isMarketUp ? 40 : 80}`}
                                fill="none"
                                stroke="url(#lineGradient)"
                                strokeWidth="3"
                                className={isMarketUp ? 'animate-pulse' : ''}
                              />
                              {/* Fill area under the line */}
                              <path
                                d={`M 0 ${isMarketUp ? 180 : 120} 
                                    Q 80 ${isMarketUp ? 140 : 160}, 160 ${isMarketUp ? 120 : 180}
                                    T 320 ${isMarketUp ? 100 : 200}
                                    T 480 ${isMarketUp ? 80 : 220}
                                    T 640 ${isMarketUp ? 60 : 180}
                                    T 800 ${isMarketUp ? 80 : 160}
                                    T 960 ${isMarketUp ? 100 : 140}
                                    T 1120 ${isMarketUp ? 70 : 170}
                                    T 1280 ${isMarketUp ? 50 : 150}
                                    T 1440 ${isMarketUp ? 80 : 120}
                                    T 1600 ${isMarketUp ? 60 : 100}
                                    T 1920 ${isMarketUp ? 40 : 80}
                                    L 1920 300 L 0 300 Z`}
                                fill={isMarketUp ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'}
                              />
                            </svg>
                            
                            {/* Market Stats Overlay - Top Right - Hidden on mobile */}
                            {/* REMOVED - Now in stats bar below */}
                            
                            {/* S&P 500 Index - Top Left - Hidden on mobile to prevent overlap */}
                            {/* REMOVED - Now in stats bar below */}
                            
                            {/* Market Indices & Portfolio Stats Bar - Non-overlapping */}
                            <div className="relative z-10 flex flex-wrap items-center justify-center sm:justify-between gap-2 sm:gap-4 mb-2 sm:mb-4 px-2 py-1.5 sm:py-2 bg-slate-800/60 backdrop-blur-sm rounded-lg border border-slate-700">
                              {/* Market Indices */}
                              <div className="flex items-center gap-2 sm:gap-4">
                                {/* S&P 500 */}
                                <div className="flex items-center gap-1 sm:gap-2 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-slate-700/50 rounded">
                                  <span className="text-[9px] sm:text-[10px] text-slate-400 font-medium">S&P</span>
                                  {usIndices?.['S&P 500'] ? (
                                    <>
                                      <span className="text-[10px] sm:text-xs font-bold text-white">{privacyMode ? '•••' : usIndices['S&P 500'].price?.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                                      <span className={`text-[9px] sm:text-[10px] font-bold ${(usIndices['S&P 500'].changePercent || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {(usIndices['S&P 500'].changePercent || 0) >= 0 ? '+' : ''}{(usIndices['S&P 500'].changePercent || 0).toFixed(2)}%
                                      </span>
                                    </>
                                  ) : marketIndex ? (
                                    <>
                                      <span className="text-[10px] sm:text-xs font-bold text-white">{privacyMode ? '•••' : marketIndex.price?.toFixed(0)}</span>
                                      <span className={`text-[9px] sm:text-[10px] font-bold ${(marketIndex.changePercent || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {(marketIndex.changePercent || 0) >= 0 ? '+' : ''}{(marketIndex.changePercent || 0).toFixed(2)}%
                                      </span>
                                    </>
                                  ) : (
                                    <span className="text-[10px] sm:text-xs text-slate-500">--</span>
                                  )}
                                </div>
                                {/* NASDAQ - Real data from Yahoo Finance */}
                                <div className="flex items-center gap-1 sm:gap-2 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-slate-700/50 rounded">
                                  <span className="text-[9px] sm:text-[10px] text-slate-400 font-medium">NDX</span>
                                  {usIndices?.NASDAQ ? (
                                    <>
                                      <span className="text-[10px] sm:text-xs font-bold text-white">{privacyMode ? '•••' : usIndices.NASDAQ.price?.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                                      <span className={`text-[9px] sm:text-[10px] font-bold ${(usIndices.NASDAQ.changePercent || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {(usIndices.NASDAQ.changePercent || 0) >= 0 ? '+' : ''}{(usIndices.NASDAQ.changePercent || 0).toFixed(2)}%
                                      </span>
                                    </>
                                  ) : (
                                    <span className="text-[10px] sm:text-xs text-slate-500">--</span>
                                  )}
                                </div>
                                {/* DOW - Real data from Yahoo Finance */}
                                <div className="hidden sm:flex items-center gap-2 px-2 py-1 bg-slate-700/50 rounded">
                                  <span className="text-[10px] text-slate-400 font-medium">DOW</span>
                                  {usIndices?.DOW ? (
                                    <>
                                      <span className="text-xs font-bold text-white">{privacyMode ? '•••' : usIndices.DOW.price?.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                                      <span className={`text-[10px] font-bold ${(usIndices.DOW.changePercent || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {(usIndices.DOW.changePercent || 0) >= 0 ? '+' : ''}{(usIndices.DOW.changePercent || 0).toFixed(2)}%
                                      </span>
                                    </>
                                  ) : (
                                    <span className="text-xs text-slate-500">--</span>
                                  )}
                                </div>
                              </div>
                              
                              {/* Portfolio Breadth - Hidden on mobile for cleaner layout */}
                              <div className="hidden sm:flex items-center gap-2 sm:gap-3 px-2 py-1 bg-slate-700/50 rounded">
                                <span className="text-[10px] text-slate-400 font-medium">Portfolio:</span>
                                <div className="flex items-center gap-1">
                                  <TrendingUp className="w-3 h-3 text-emerald-400" />
                                  <span className="text-xs font-bold text-emerald-400">{upStocks}</span>
                                </div>
                                <div className="w-16 h-1.5 bg-slate-600 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500"
                                    style={{ width: `${upPercent}%` }}
                                  />
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-xs font-bold text-red-400">{downStocks}</span>
                                  <TrendingDown className="w-3 h-3 text-red-400" />
                                </div>
                                <span className={`text-[10px] font-bold ${isMarketUp ? 'text-emerald-400' : 'text-red-400'}`}>
                                  ({avgChange >= 0 ? '+' : ''}{avgChange.toFixed(1)}%)
                                </span>
                              </div>
                              
                              {/* Portfolio Performance $ - Hidden on mobile */}
                              <div className="hidden sm:flex items-center gap-3 px-2 py-1 bg-gradient-to-r from-slate-700/50 to-slate-600/50 rounded border border-slate-600/50">
                                {/* Market Returns Label */}
                                <div className="flex items-center gap-1 pr-2 border-r border-slate-600">
                                  <TrendingUp className="w-3 h-3 text-teal-400" />
                                  <span className="text-[8px] text-teal-400 uppercase tracking-wide font-semibold">Market</span>
                                </div>
                                {/* Today's Gain */}
                                <div className="flex flex-col items-center">
                                  <span className="text-[8px] text-slate-400 uppercase">Today</span>
                                  <span className={`text-[11px] font-bold ${parseFloat(performance?.total?.['1D']?.change || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {privacyMode ? '•••' : `${parseFloat(performance?.total?.['1D']?.change || 0) >= 0 ? '+' : ''}$${Math.abs(parseFloat(performance?.total?.['1D']?.change || 0)).toLocaleString(undefined, {maximumFractionDigits: 0})}`}
                                  </span>
                                </div>
                                <div className="w-px h-6 bg-slate-600"></div>
                                {/* This Week */}
                                <div className="flex flex-col items-center">
                                  <span className="text-[8px] text-slate-400 uppercase">Week</span>
                                  <span className={`text-[11px] font-bold ${parseFloat(performance?.total?.['1W']?.change || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {privacyMode ? '•••' : `${parseFloat(performance?.total?.['1W']?.change || 0) >= 0 ? '+' : ''}$${Math.abs(parseFloat(performance?.total?.['1W']?.change || 0)).toLocaleString(undefined, {maximumFractionDigits: 0})}`}
                                  </span>
                                </div>
                                <div className="w-px h-6 bg-slate-600"></div>
                                {/* This Month */}
                                <div className="flex flex-col items-center">
                                  <span className="text-[8px] text-slate-400 uppercase">Month</span>
                                  <span className={`text-[11px] font-bold ${parseFloat(performance?.total?.['1M']?.change || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {privacyMode ? '•••' : `${parseFloat(performance?.total?.['1M']?.change || 0) >= 0 ? '+' : ''}$${Math.abs(parseFloat(performance?.total?.['1M']?.change || 0)).toLocaleString(undefined, {maximumFractionDigits: 0})}`}
                                  </span>
                                </div>
                                <div className="w-px h-6 bg-slate-600"></div>
                                {/* All Time */}
                                <div className="flex flex-col items-center">
                                  <span className="text-[8px] text-slate-400 uppercase">All</span>
                                  <span className={`text-[11px] font-bold ${parseFloat(performance?.total?.allTime?.change || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {privacyMode ? '•••' : `${parseFloat(performance?.total?.allTime?.change || 0) >= 0 ? '+' : ''}$${Math.abs(parseFloat(performance?.total?.allTime?.change || 0)).toLocaleString(undefined, {maximumFractionDigits: 0})}`}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                      
                      {/* Fullscreen Side Panels - Market Movers & News */}
                      {isMarketLiveExpanded && (
                        <>
                          {/* Top Ticker - Scrolling Headlines */}
                          <NewsTicker 
                            marketNews={tickerNews} 
                            marketStatus={marketStatus} 
                            lastUpdated={stocks.length > 0 ? stocks[0]?.lastUpdated : null}
                          />
                          
                          {/* World Clocks - Financial Cities (Desktop only) */}
                          <div className="hidden sm:block">
                            <WorldClocks currentTime={currentTime} />
                          </div>
                          
                          {/* Mobile View Toggle - only show in fullscreen mode - positioned absolutely at top */}
                          {isMarketLiveExpanded && (
                          <div className="sm:hidden flex justify-center z-50 absolute top-14 left-0 right-0 pointer-events-auto">
                            <div className="flex bg-slate-800/90 rounded-lg p-0.5 border border-slate-600 shadow-lg">
                              <button
                                onPointerDown={(e) => {
                                  e.stopPropagation();
                                  setMobileMarketView('bubbles');
                                }}
                                type="button"
                                className={`px-3 py-1 text-[10px] font-semibold rounded-md transition-all touch-manipulation ${
                                  mobileMarketView === 'bubbles'
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : 'text-slate-400'
                                }`}
                              >
                                📊 Stocks
                              </button>
                              <button
                                onPointerDown={(e) => {
                                  e.stopPropagation();
                                  setMobileMarketView('panels');
                                }}
                                type="button"
                                className={`px-3 py-1 text-[10px] font-semibold rounded-md transition-all touch-manipulation ${
                                  mobileMarketView === 'panels'
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : 'text-slate-400'
                                }`}
                              >
                                📰 News
                              </button>
                            </div>
                          </div>
                          )}
                          
                          {/* Mobile Panels - Two columns side by side when "News" is selected */}
                          {mobileMarketView === 'panels' && (
                            <div className="sm:hidden flex flex-row gap-2 px-2 w-full absolute inset-x-0 top-36 bottom-4">
                              {/* Left Column - Movers */}
                              <div className="flex-1 bg-slate-800/90 backdrop-blur-sm rounded-lg border border-slate-700 overflow-hidden flex flex-col">
                                {/* Movers Header with Toggle */}
                                <div className="px-1.5 py-1 bg-slate-900/50 border-b border-slate-700 shrink-0">
                                  <div className="flex bg-slate-700/50 rounded p-0.5">
                                    <button
                                      onClick={() => setMobileMoversMode('market')}
                                      className={`flex-1 text-[8px] font-medium py-0.5 px-1 rounded transition-all ${
                                        mobileMoversMode === 'market'
                                          ? 'bg-blue-600 text-white shadow-sm'
                                          : 'text-slate-400'
                                      }`}
                                    >
                                      Market
                                    </button>
                                    <button
                                      onClick={() => setMobileMoversMode('portfolio')}
                                      className={`flex-1 text-[8px] font-medium py-0.5 px-1 rounded transition-all ${
                                        mobileMoversMode === 'portfolio'
                                          ? 'bg-blue-600 text-white shadow-sm'
                                          : 'text-slate-400'
                                      }`}
                                    >
                                      My Stocks
                                    </button>
                                  </div>
                                </div>
                                <div className="flex-1 overflow-auto p-1 flex flex-col">
                                  {/* Gainers */}
                                  <div className="flex flex-col">
                                    <p className="text-[9px] text-emerald-400 font-semibold px-1 py-0.5 bg-slate-800/95 shrink-0 sticky top-0">
                                      🔺 GAINERS ({(mobileMoversMode === 'market' ? sortedMarketMovers : portfolioMovers)?.gainers?.length || 0})
                                    </p>
                                    <div>
                                      {((mobileMoversMode === 'market' ? sortedMarketMovers : portfolioMovers)?.gainers || []).map((stock, i) => (
                                        <div 
                                          key={i} 
                                          className="flex justify-between items-center px-1 py-[2px] bg-emerald-500/10 rounded mb-0.5 cursor-pointer active:bg-emerald-500/30"
                                          onClick={() => setMobileSelectedStock(stock)}
                                        >
                                          <span className="text-[10px] font-medium text-slate-200">{stock.symbol}</span>
                                          <span className="text-[10px] font-bold text-emerald-400">+{stock.changePercent?.toFixed(1)}%</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  {/* Losers */}
                                  <div className="flex flex-col mt-1">
                                    <p className="text-[9px] text-red-400 font-semibold px-1 py-0.5 bg-slate-800/95 shrink-0 sticky top-0">
                                      🔻 LOSERS ({(mobileMoversMode === 'market' ? sortedMarketMovers : portfolioMovers)?.losers?.length || 0})
                                    </p>
                                    <div>
                                      {((mobileMoversMode === 'market' ? sortedMarketMovers : portfolioMovers)?.losers || []).map((stock, i) => (
                                        <div 
                                          key={i} 
                                          className="flex justify-between items-center px-1 py-[2px] bg-red-500/10 rounded mb-0.5 cursor-pointer active:bg-red-500/30"
                                          onClick={() => setMobileSelectedStock(stock)}
                                        >
                                          <span className="text-[10px] font-medium text-slate-200">{stock.symbol}</span>
                                          <span className="text-[10px] font-bold text-red-400">{stock.changePercent?.toFixed(1)}%</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Right Column - News */}
                              <div className="flex-1 bg-slate-800/90 backdrop-blur-sm rounded-lg border border-slate-700 overflow-hidden flex flex-col">
                                {/* News Header with Toggle */}
                                <div className="px-1.5 py-1 bg-slate-900/50 border-b border-slate-700 shrink-0">
                                  <div className="flex bg-slate-700/50 rounded p-0.5">
                                    <button
                                      onClick={() => setMobileNewsMode('market')}
                                      className={`flex-1 text-[8px] font-medium py-0.5 px-1 rounded transition-all ${
                                        mobileNewsMode === 'market'
                                          ? 'bg-blue-600 text-white shadow-sm'
                                          : 'text-slate-400'
                                      }`}
                                    >
                                      Market
                                    </button>
                                    <button
                                      onClick={() => setMobileNewsMode('portfolio')}
                                      className={`flex-1 text-[8px] font-medium py-0.5 px-1 rounded transition-all ${
                                        mobileNewsMode === 'portfolio'
                                          ? 'bg-blue-600 text-white shadow-sm'
                                          : 'text-slate-400'
                                      }`}
                                    >
                                      My Stocks
                                    </button>
                                  </div>
                                </div>
                                <div className="flex-1 overflow-auto">
                                  {(mobileNewsMode === 'market' ? marketNews : portfolioNews || []).map((news, i) => (
                                    <a 
                                      key={i} 
                                      href={news.url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="block px-1.5 py-1 border-b border-slate-700/30 hover:bg-slate-700/50"
                                    >
                                      {mobileNewsMode === 'portfolio' && news.symbol && (
                                        <span className="text-[8px] font-bold text-blue-400 mr-1">${news.symbol}</span>
                                      )}
                                      <p className="text-[10px] font-medium text-slate-200 line-clamp-2 leading-tight">{news.headline}</p>
                                      <p className="text-[8px] text-slate-500 mt-0.5">{news.source}</p>
                                    </a>
                                  ))}
                                </div>
                              </div>
                              
                              {/* Mobile Stock Detail Popup */}
                              {mobileSelectedStock && (
                                <div 
                                  className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50"
                                  onClick={() => setMobileSelectedStock(null)}
                                >
                                  <div 
                                    className="bg-slate-900 border border-slate-600 rounded-xl shadow-2xl p-4 mx-4 max-w-[300px] w-full"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {/* Header */}
                                    <div className="flex items-center justify-between border-b border-slate-700 pb-3 mb-3">
                                      <div>
                                        <span className="text-lg font-bold text-white">{mobileSelectedStock.symbol}</span>
                                        <p className="text-xs text-slate-400 mt-0.5">{mobileSelectedStock.name}</p>
                                      </div>
                                      <span className={`text-lg font-bold ${mobileSelectedStock.changePercent >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {mobileSelectedStock.changePercent >= 0 ? '+' : ''}{mobileSelectedStock.changePercent?.toFixed(2)}%
                                      </span>
                                    </div>
                                    
                                    {/* Price Info */}
                                    <div className="space-y-2">
                                      <div className="flex justify-between items-center">
                                        <span className="text-xs text-slate-400">Current Price</span>
                                        <span className="text-sm font-semibold text-white">${mobileSelectedStock.price?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                      </div>
                                      <div className="flex justify-between items-center">
                                        <span className="text-xs text-slate-400">Change</span>
                                        <span className={`text-sm font-semibold ${mobileSelectedStock.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                          {mobileSelectedStock.change >= 0 ? '+' : ''}${mobileSelectedStock.change?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                        </span>
                                      </div>
                                      <div className="flex justify-between items-center">
                                        <span className="text-xs text-slate-400">Day Range</span>
                                        <span className="text-sm text-slate-300">
                                          ${mobileSelectedStock.dayLow?.toFixed(2)} - ${mobileSelectedStock.dayHigh?.toFixed(2)}
                                        </span>
                                      </div>
                                      <div className="flex justify-between items-center">
                                        <span className="text-xs text-slate-400">52-Week High</span>
                                        <span className="text-sm text-slate-300">${mobileSelectedStock.week52High?.toFixed(2)}</span>
                                      </div>
                                      <div className="flex justify-between items-center">
                                        <span className="text-xs text-slate-400">Open</span>
                                        <span className="text-sm text-slate-300">${mobileSelectedStock.openPrice?.toFixed(2)}</span>
                                      </div>
                                      <div className="flex justify-between items-center">
                                        <span className="text-xs text-slate-400">Prev. Close</span>
                                        <span className="text-sm text-slate-300">${mobileSelectedStock.previousClose?.toFixed(2)}</span>
                                      </div>
                                    </div>
                                    
                                    {/* Close button */}
                                    <button 
                                      className="mt-4 w-full py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium rounded-lg transition-colors"
                                      onClick={() => setMobileSelectedStock(null)}
                                    >
                                      Close
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Desktop Panels - Always visible on desktop */}
                          <div className="hidden sm:block">
                            <MarketMoversPanel marketMovers={sortedMarketMovers} portfolioMovers={portfolioMovers} />
                          </div>
                          <div className="hidden sm:block">
                            <MarketNewsPanel marketNews={marketNews} portfolioNews={portfolioNews} />
                          </div>
                        </>
                      )}
                      
                      <div className={`relative flex flex-wrap justify-center pt-0 ${isMarketLiveExpanded ? 'gap-1.5 sm:gap-4 content-center items-center sm:ml-60 sm:mr-60 sm:mt-40 sm:mb-16 px-1 sm:px-0 max-sm:absolute max-sm:inset-x-1 max-sm:top-[125px] max-sm:bottom-0 max-sm:content-start max-sm:items-start max-sm:overflow-y-auto max-sm:pb-2' : 'gap-3 items-end'} ${isMarketLiveExpanded && mobileMarketView === 'panels' ? 'hidden sm:flex' : ''}`}>
                        {(() => {
                          // Helper function to get volatility by stock type
                          const getStockVolatility = (symbol) => {
                            if (symbol.includes('BINANCE') || symbol.includes('BTC') || symbol.includes('ETH') || symbol.includes('XRP') || symbol.includes('SOL')) return 2.5;
                            if (['TSLA', 'NVDA', 'AMD', 'SHOP', 'SQ', 'COIN'].includes(symbol)) return 1.8;
                            if (['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'META'].includes(symbol)) return 1.2;
                            if (['JPM', 'WMT', 'JNJ', 'PG', 'KO', 'V', 'MA'].includes(symbol)) return 0.7;
                            return 1.0;
                          };
                          
                          // Create deterministic variation
                          const getVariationFactor = (symbol, period) => {
                            let hash = 0;
                            const str = symbol + period;
                            for (let i = 0; i < str.length; i++) {
                              hash = ((hash << 5) - hash) + str.charCodeAt(i);
                              hash = hash & hash;
                            }
                            return 0.3 + (Math.abs(hash % 140) / 100);
                          };
                          
                          // Calculate metrics for each stock based on selected period
                          // Filter out non-market assets (CASH, JEWELRY, etc.) from Market Live bubbles
                          const stocksWithMetrics = stocks
                            .filter(stock => isMarketAsset(stock))
                            .map(stock => {
                            const totalValue = stock.currentPrice * stock.shares;
                            const costTotal = stock.costBasis * stock.shares;
                            const allTimeGainLoss = totalValue - costTotal;
                            const allTimeGainLossPercent = costTotal > 0 ? ((allTimeGainLoss / costTotal) * 100) : 0;
                            
                            let gainLossPercent, gainLoss, hasRealDailyData;
                            
                            // Check if this stock has REAL daily change data from the API
                            // Note: Some stocks (mutual funds, precious metals) get price from Alpha Vantage fallback
                            // which provides price but NOT daily change data. These show as 0 dailyChange.
                            // Check if we have NO daily change data (null/undefined means missing, 0 is valid data meaning no change)
                            // Only consider it "no data" if dailyChangePercent is specifically undefined/null AND there's no previousClose
                            const hasNoDailyChangeData = (stock.dailyChangePercent === undefined || stock.dailyChangePercent === null) && 
                                                          (stock.previousClose === undefined || stock.previousClose === null);
                            // For stocks with no daily change data, we can't show 1D/1W/1M/1Y performance
                            // They still have valid price data for ALL time calculation
                            
                            if (marketLivePeriod === 'ALL') {
                              // Use actual all-time return - this is REAL data from cost basis vs current price
                              gainLossPercent = allTimeGainLossPercent;
                              gainLoss = allTimeGainLoss;
                              hasRealDailyData = true; // ALL time is always real (based on purchase price)
                            } else if (hasNoDailyChangeData) {
                              // No daily change data available from API
                              // Show N/A for time-based periods (1D, 1W, 1M, 1Y)
                              gainLossPercent = 0;
                              gainLoss = 0;
                              hasRealDailyData = false;
                            } else if (marketLivePeriod === '1D') {
                              // Use REAL daily change data from API
                              gainLossPercent = stock.dailyChangePercent || 0;
                              gainLoss = (stock.dailyChange || 0) * stock.shares;
                              hasRealDailyData = true;
                            } else {
                              // For 1W, 1M, 1Y - estimate based on daily change scaled
                              // This is still estimated but uses real daily data as a base
                              const dailyPct = stock.dailyChangePercent || 0;
                              const volatility = getStockVolatility(stock.symbol);
                              const variation = getVariationFactor(stock.symbol, marketLivePeriod);
                              
                              // Scale daily change to estimate weekly/monthly/yearly
                              const scaleFactor = {
                                '1W': 5 * variation,  // ~5 trading days
                                '1M': 22 * variation, // ~22 trading days
                                '1Y': 252 * variation * 0.3 // ~252 trading days, dampened
                              };
                              
                              // Use real direction from daily, scaled by time period
                              const baseChange = dailyPct !== 0 ? dailyPct : (allTimeGainLossPercent >= 0 ? 0.5 : -0.5);
                              gainLossPercent = baseChange * scaleFactor[marketLivePeriod] * volatility;
                              gainLoss = (totalValue * gainLossPercent) / 100;
                              hasRealDailyData = true; // Estimated data is "available"
                            }
                            
                            // Bubble size based on POSITION VALUE (total value of your holding)
                            // This shows your largest positions as the biggest bubbles
                            const volume = totalValue;
                            
                            return { ...stock, gainLoss, gainLossPercent, totalValue, volume, hasRealDailyData, allTimeGainLoss, allTimeGainLossPercent };
                          });
                          
                          // Period label for display
                          const periodLabel = marketLivePeriod === '1D' 
                            ? (marketStatus.status === 'open' ? 'Today (Live)' : 'Last Close (Real)') 
                            : marketLivePeriod === '1W' ? 'This Week (Est.)' 
                            : marketLivePeriod === '1M' ? 'This Month (Est.)' 
                            : marketLivePeriod === '1Y' ? 'This Year (Est.)'
                            : 'All Time (Real)';
                          
                          // Get max volume for sizing
                          const maxVolume = Math.max(...stocksWithMetrics.map(s => s.volume));
                          
                          // Check if mobile (window width < 640px)
                          const isMobile = window.innerWidth < 640;
                          
                          // Bubble sizes: MAXIMIZE for mobile fullscreen to fill screen space
                          // Mobile fullscreen: larger bubbles to use all available screen real estate
                          // Mobile collapsed: medium, Desktop: larger for readability
                          const isMobileFullscreen = isMobile && isMarketLiveExpanded;
                          const stockCount = stocksWithMetrics.length;
                          // Dynamically size based on stock count - fewer stocks = bigger bubbles
                          // MAXIMIZE sizes for mobile fullscreen to fill the entire screen
                          // Screen: ~390px width, ~650px available height for bubbles
                          // For 45 stocks: 6 per row x 8 rows, larger bubbles to fill space
                          const mobileMinSize = stockCount > 40 ? 62 : (stockCount > 30 ? 70 : 78);
                          const mobileMaxSize = stockCount > 40 ? 80 : (stockCount > 30 ? 92 : 102);
                          const minSize = isMobileFullscreen ? mobileMinSize : (isMarketLiveExpanded ? 80 : (isMobile ? 55 : 50));
                          const maxSize = isMobileFullscreen ? mobileMaxSize : (isMarketLiveExpanded ? 220 : (isMobile ? 95 : 120));
                          
                          // Sort by percentage change
                          // Desktop: losers (left) to gainers (right) - ascending
                          // Mobile: gainers (top) to losers (bottom) - descending
                          const sortedStocks = [...stocksWithMetrics]
                            .sort((a, b) => isMobile 
                              ? b.gainLossPercent - a.gainLossPercent  // Descending: gainers first (top)
                              : a.gainLossPercent - b.gainLossPercent  // Ascending: losers first (left)
                            );
                          
                          return sortedStocks.map((stock, index) => {
                            // Show N/A for stocks without real data (except ALL which uses cost basis)
                            const hasNoData = marketLivePeriod !== 'ALL' && stock.hasRealDailyData === false;
                            // Use a very small threshold - show color for any meaningful change
                            const isUp = !hasNoData && stock.gainLossPercent > 0.001; // More than +0.001% is up (green)
                            const isDown = !hasNoData && stock.gainLossPercent < -0.001; // Less than -0.001% is down (red)
                            const isNeutral = !hasNoData && !isUp && !isDown; // Essentially zero change
                            const size = minSize + ((stock.volume / maxVolume) * (maxSize - minSize));
                            
                            // Determine colors based on performance
                            // Use subtle backgrounds with readable text (same style for both expanded and regular)
                            let bubbleClass, shadowClass;
                            if (hasNoData) {
                              // No data available - show with dashed border
                              bubbleClass = isMobile 
                                ? 'bg-gradient-to-br from-slate-400 to-slate-500 border-2 border-dashed border-slate-300'
                                : 'bg-slate-300/20 border-2 border-dashed border-slate-400';
                              shadowClass = 'shadow-md shadow-slate-400/20';
                            } else if (isUp) {
                              bubbleClass = isMobile
                                ? 'bg-gradient-to-br from-emerald-500 to-teal-600'
                                : 'bg-emerald-400/20 border border-emerald-500/30';
                              shadowClass = isMobile ? 'shadow-lg shadow-emerald-500/30' : 'shadow-md';
                            } else if (isDown) {
                              bubbleClass = isMobile
                                ? 'bg-gradient-to-br from-red-500 to-orange-600'
                                : 'bg-red-400/10 border border-red-400/20';
                              shadowClass = isMobile ? 'shadow-lg shadow-red-500/30' : 'shadow-md';
                            } else {
                              bubbleClass = isMobile
                                ? 'bg-gradient-to-br from-slate-500 to-slate-600'
                                : 'bg-slate-400/15 border border-slate-400/30';
                              shadowClass = isMobile ? 'shadow-lg shadow-slate-500/30' : 'shadow-md';
                            }
                            
                            // Check if this stock was just updated
                            const wasJustUpdated = recentlyUpdatedStocks.has(stock.id);
                            
                            return (
                              <div
                                key={stock.id}
                                className="relative group cursor-pointer"
                                style={{
                                  width: `${size}px`,
                                  height: `${size}px`,
                                }}
                              >
                                {/* Bubble with pulse animation when price updates */}
                                <div
                                  className={`absolute inset-0 rounded-lg transition-all duration-300 group-hover:scale-110 ${bubbleClass} ${shadowClass} ${wasJustUpdated ? 'animate-pulse ring-2 ring-white ring-opacity-75' : ''}`}
                                >
                                  {/* Stock info */}
                                  <div className="absolute inset-0 flex flex-col items-center justify-center p-0 leading-none">
                                    <span className={`font-extrabold truncate max-w-full drop-shadow-sm ${
                                      isMobileFullscreen
                                        ? 'text-xs text-slate-900'
                                        : isMobile 
                                          ? 'text-sm text-slate-900'
                                          : 'text-base text-slate-100'
                                    }`}>
                                      {stock.symbol.replace('BINANCE:', '').substring(0, isMobileFullscreen ? 6 : (isMobile ? 5 : 6))}
                                    </span>
                                    <span className={`font-extrabold leading-tight ${
                                      isMobileFullscreen
                                        ? 'text-[10px] text-slate-800'
                                        : isMobile 
                                          ? 'text-xs text-slate-800'
                                          : `text-sm ${stock.gainLossPercent >= 0 ? 'text-emerald-300' : 'text-red-300'}`
                                    }`}>
                                      {hasNoData ? 'N/A' : `${stock.gainLossPercent >= 0 ? '+' : ''}${stock.gainLossPercent.toFixed(1)}%`}
                                    </span>
                                    {/* Show total gain/loss (all-time) - desktop only */}
                                    {!isMobile && !hasNoData && (
                                      <span className={`font-extrabold text-xs leading-tight ${stock.allTimeGainLoss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        {privacyMode ? '•••' : `${stock.allTimeGainLoss >= 0 ? '+' : ''}$${Math.abs(stock.allTimeGainLoss) >= 1000 ? (stock.allTimeGainLoss/1000).toFixed(1) + 'K' : stock.allTimeGainLoss.toFixed(0)}`}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {/* Tooltip on hover - appears below bubble to avoid header overlap - hidden on mobile to prevent stuck tooltips */}
                                <div className="hidden sm:block absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 bg-slate-900 rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 whitespace-nowrap">
                                  <div className="text-xs text-white font-bold">{stock.symbol}</div>
                                  <div className="text-[10px] text-slate-300">{stock.name || stock.symbol}</div>
                                  <div className={`text-xs font-semibold ${isUp ? 'text-emerald-400' : isDown ? 'text-red-400' : 'text-slate-400'}`}>
                                    {stock.gainLossPercent >= 0 ? '+' : ''}${Math.abs(stock.gainLoss).toFixed(2)} ({stock.gainLossPercent.toFixed(2)}%)
                                  </div>
                                  <div className="text-[10px] text-slate-400">{periodLabel}</div>
                                  <div className="text-[10px] text-slate-400">Value: ${privacyMode ? '•••' : (stock.totalValue/1000).toFixed(1)}K</div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                      {/* Legend - Fixed at bottom in fullscreen mode */}
                      {/* Hide legend on mobile fullscreen to maximize bubble space */}
                      <div className={`${isMarketLiveExpanded ? 'absolute bottom-2 left-0 right-0 sm:bottom-4 max-sm:hidden' : 'relative'} flex flex-wrap justify-center items-center gap-3 sm:gap-4 text-[10px] sm:text-xs py-2 ${mobileMarketView === 'panels' ? 'hidden sm:flex' : ''}`}>
                        {/* Mobile legend (vertical: top to bottom) */}
                        <div className="flex sm:hidden items-center gap-1">
                          <div className="w-3 h-3 rounded bg-gradient-to-br from-emerald-500 to-teal-600"></div>
                          <span className="text-slate-400">Gainers</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-gradient-to-br from-slate-500 to-slate-600"></div>
                          <span className="text-slate-400">No Change</span>
                        </div>
                        <div className="flex sm:hidden items-center gap-1">
                          <div className="w-3 h-3 rounded bg-gradient-to-br from-red-500 to-orange-600"></div>
                          <span className="text-slate-400">Losers</span>
                        </div>
                        {/* Desktop legend (horizontal: left to right) */}
                        <div className="hidden sm:flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-gradient-to-br from-red-500 to-orange-600"></div>
                          <span className="text-slate-400">Losers</span>
                        </div>
                        <div className="hidden sm:flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-gradient-to-br from-emerald-500 to-teal-600"></div>
                          <span className="text-slate-400">Gainers</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="flex items-center gap-0.5">
                            <div className="w-2 h-2 rounded bg-slate-500"></div>
                            <div className="w-3 h-3 rounded bg-slate-500"></div>
                          </div>
                          <span className="text-slate-400">Size = Value</span>
                        </div>
                        {/* Footer version */}
                        {footerVersion && (
                          <span 
                            className="text-slate-500 ml-2 sm:ml-4 cursor-pointer hover:text-cyan-400 transition-colors"
                            onClick={() => setShowChangelog(true)}
                            data-testid="version-footer-link"
                            title="View changelog"
                          >
                            | {footerVersion}
                          </span>
                        )}
                      </div>
                      
                      {/* CSS for animations */}
                      <style>{`
                        @keyframes floatUp {
                          0%, 100% { transform: translateY(0px); }
                          50% { transform: translateY(-15px); }
                        }
                        @keyframes floatDown {
                          0%, 100% { transform: translateY(0px); }
                          50% { transform: translateY(15px); }
                        }
                        @keyframes floatNeutral {
                          0%, 100% { transform: scale(1); }
                          50% { transform: scale(1.05); }
                        }
                      `}</style>
                    </div>
                  )}
                </div>
              </div>
            );
          }

          // Platform Performance Panel + Performance Leaders (Combined Row) - Hidden on mobile
          if (panelId === 'platform') {
            return (
              <div key="platform-leaders-row" className={`hidden sm:grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4 ${mobilePreview ? 'mb-2' : 'mb-2 sm:mb-4'}`}>
                <PlatformPerformancePanel
                  platforms={platforms}
                  performance={performance}
                  showPlatformPerformance={showPlatformPerformance}
                  togglePlatformPerformance={togglePlatformPerformance}
                  selectedPlatform={selectedPlatform}
                  setSelectedPlatform={setSelectedPlatform}
                  setShowStockList={setShowStockList}
                  mobilePreview={mobilePreview}
                  formatCurrencyPrivate={formatCurrencyPrivate}
                  movePanelUp={movePanelUp}
                  movePanelDown={movePanelDown}
                  isFirst={isFirst}
                  isLast={isLast}
                  platformSortColumn={platformSortColumn}
                  platformSortDirection={platformSortDirection}
                  handlePlatformSort={handlePlatformSort}
                  getSortedPlatforms={getSortedPlatforms}
                  userEmail={sessionStorage.getItem('portfolio_user_email')}
                />
                <PerformanceLeadersPanel
                  stocks={stocks}
                  showMoversChart={showMoversChart}
                  toggleMoversChart={toggleMoversChart}
                  moversPeriod={moversPeriod}
                  setMoversPeriod={setMoversPeriod}
                  mobilePreview={mobilePreview}
                  marketStatus={marketStatus}
                  formatCurrencyPrivate={formatCurrencyPrivate}
                  movePanelUp={movePanelUp}
                  movePanelDown={movePanelDown}
                  isFirst={isFirst}
                  isLast={isLast}
                />
              </div>
            );
          }

          // Skip 'leaders' since it's now combined with 'platform'
          if (panelId === 'leaders') {
            return null;
          }

          // Portfolio Diversity Panel
          if (panelId === 'diversity') {
            return (
              <PortfolioDiversityPanel
                key="diversity"
                stocks={stocks}
                showDiversity={showDiversity}
                toggleDiversity={toggleDiversity}
                mobilePreview={mobilePreview}
                formatCurrencyPrivate={formatCurrencyPrivate}
                privacyMode={privacyMode}
                hideMarketValue={hideMarketValue}
                movePanelUp={movePanelUp}
                movePanelDown={movePanelDown}
                isFirst={isFirst}
                isLast={isLast}
              />
            );
          }

          // Stock Analytics Panel
          if (panelId === 'analytics') {
            return (
              <StockAnalyticsPanel
                key="analytics"
                stocks={stocks}
                showStockAnalytics={showStockAnalytics}
                toggleStockAnalytics={toggleStockAnalytics}
                mobilePreview={mobilePreview}
                summary={summary}
                selectedPlatform={selectedPlatform}
                selectedStockPopup={selectedStockPopup}
                setSelectedStockPopup={setSelectedStockPopup}
                formatCurrencyPrivate={formatCurrencyPrivate}
                privacyMode={privacyMode}
                movePanelUp={movePanelUp}
                movePanelDown={movePanelDown}
                isFirst={isFirst}
                isLast={isLast}
                analyticsSortColumn={analyticsSortColumn}
                analyticsSortDirection={analyticsSortDirection}
                handleAnalyticsSort={handleAnalyticsSort}
                getAnalyticsSortedStocks={getAnalyticsSortedStocks}
              />
            );
          }

          // Stocks Table Panel - Hidden on mobile
          if (panelId === 'stocks') {
            return (
              <div key="stocks" className="hidden sm:block">
              <YourStocksPanel
                stocks={stocks.filter(s => isMarketAsset(s) || s.investmentType === 'MUNICIPAL')}
                sortedStocks={sortedStocks}
                showStockList={showStockList}
                toggleStockList={toggleStockList}
                selectedPlatform={selectedPlatform}
                setSelectedPlatform={setSelectedPlatform}
                mobilePreview={mobilePreview}
                formatCurrencyPrivate={formatCurrencyPrivate}
                lastUpdate={lastUpdate}
                isLoading={isLoading}
                handleRefresh={handleRefresh}
                setIsAddingStock={setIsAddingStock}
                movePanelUp={movePanelUp}
                movePanelDown={movePanelDown}
                isFirst={isFirst}
                isLast={isLast}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                handleSort={handleSort}
                editingId={editingId}
                editForm={editForm}
                setEditForm={setEditForm}
                handleEditStart={handleEditStart}
                handleEditCancel={handleEditCancel}
                handleEditSave={handleEditSave}
                handleRefreshSingleStock={handleRefreshSingleStock}
                handleDeleteStock={handleDeleteStock}
                refreshingStockId={refreshingStockId}
                calculateMetrics={calculateMetrics}
                isViewer={isViewer}
                onExport={handleExportPortfolio}
                onImport={handleImportPortfolio}
                isExporting={isExporting}
                isImporting={isImporting}
              />
              </div>
            );
          }

          // Non-Market Assets Panel
          if (panelId === 'nonmarket') {
            return (
              <NonMarketAssetsPanel
                key="nonmarket"
                stocks={stocks}
                formatCurrencyPrivate={formatCurrencyPrivate}
                privacyMode={privacyMode}
                movePanelUp={movePanelUp}
                movePanelDown={movePanelDown}
                isFirst={isFirst}
                isLast={isLast}
                isViewer={isViewer}
                onAddStock={async (stockData) => {
                  try {
                    const userEmail = sessionStorage.getItem('portfolio_user_email');
                    if (!userEmail) {
                      toast({ title: "Error", description: "Please log in first", variant: "destructive" });
                      return;
                    }
                    console.log('Adding asset:', stockData);
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portfolio?user_email=${encodeURIComponent(userEmail)}`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(stockData)
                    });
                    console.log('Response status:', response.status);
                    if (response.ok) {
                      await handleRefresh();
                      toast({ title: "Success", description: "Asset added successfully" });
                    } else {
                      const error = await response.json();
                      console.error('Add asset error:', error);
                      toast({ title: "Error", description: error.detail || "Failed to add asset", variant: "destructive" });
                    }
                  } catch (error) {
                    console.error('Add asset exception:', error);
                    toast({ title: "Error", description: "Failed to add asset", variant: "destructive" });
                  }
                }}
                onEditStock={async (id, stockData) => {
                  try {
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portfolio/${id}`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(stockData)
                    });
                    if (response.ok) {
                      await handleRefresh();
                      toast({ title: "Success", description: "Asset updated successfully" });
                    } else {
                      const error = await response.json();
                      toast({ title: "Error", description: error.detail || "Failed to update asset", variant: "destructive" });
                    }
                  } catch (error) {
                    toast({ title: "Error", description: "Failed to update asset", variant: "destructive" });
                  }
                }}
                onDeleteStock={async (id) => {
                  try {
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portfolio/${id}`, {
                      method: 'DELETE'
                    });
                    if (response.ok) {
                      await handleRefresh();
                      toast({ title: "Success", description: "Asset deleted successfully" });
                    } else {
                      const error = await response.json();
                      toast({ title: "Error", description: error.detail || "Failed to delete asset", variant: "destructive" });
                    }
                  } catch (error) {
                    toast({ title: "Error", description: "Failed to delete asset", variant: "destructive" });
                  }
                }}
                mobilePreview={mobilePreview}
                API_URL={process.env.REACT_APP_BACKEND_URL}
                userEmail={sessionStorage.getItem('portfolio_user_email')}
                isFullscreen={isNonMarketExpanded}
                onToggleFullscreen={setIsNonMarketExpanded}
                onRefresh={handleRefresh}
              />
            );
          }

          return null;
        })}
        
        {/* Stock Certificate - At bottom of content */}
        {/* More prominent when all panels are collapsed on mobile */}
        {(() => {
          const allPanelsCollapsed = !showMarketLive && !showDiversity && !showPlatformPerformance && !showStockAnalytics && !showMoversChart && !showStockList;
          return (
            <div className={`mx-auto px-4 transition-all duration-300 ${
              allPanelsCollapsed 
                ? 'mt-4 sm:mt-8 mb-2 sm:mb-4 opacity-40 sm:opacity-[0.20]' 
                : 'mt-8 mb-4 opacity-[0.20]'
            } max-w-3xl`}>
              <div className={`border-4 border-double border-slate-500 bg-slate-100/50 rounded ${
                allPanelsCollapsed ? 'p-2 sm:p-6' : 'p-4 sm:p-6'
              }`}>
                <div className={`border-2 border-slate-400 rounded ${
                  allPanelsCollapsed ? 'p-2 sm:p-4' : 'p-3 sm:p-4'
                }`}>
                  <div className={`text-center ${allPanelsCollapsed ? 'space-y-0.5 sm:space-y-2' : 'space-y-1 sm:space-y-2'}`}>
                    <p className={`tracking-[0.2em] sm:tracking-[0.3em] text-slate-600 uppercase ${
                      allPanelsCollapsed ? 'text-[8px] sm:text-xs' : 'text-[10px] sm:text-xs'
                    }`}>This Ledger Certifies Ownership Of</p>
                    <p className={`font-serif font-bold text-slate-700 tracking-wide ${
                      allPanelsCollapsed ? 'text-2xl sm:text-6xl' : 'text-4xl sm:text-6xl'
                    }`}>1,000,000</p>
                    <p className={`tracking-[0.15em] sm:tracking-[0.2em] text-slate-600 uppercase ${
                      allPanelsCollapsed ? 'text-xs sm:text-xl' : 'text-base sm:text-xl'
                    }`}>Fully Paid Shares</p>
                    <div className={`flex items-center justify-center pt-1 sm:pt-2 ${
                      allPanelsCollapsed ? 'gap-1 sm:gap-4' : 'gap-2 sm:gap-4'
                    }`}>
                      <div className={`h-px bg-slate-400 ${allPanelsCollapsed ? 'w-8 sm:w-24' : 'w-12 sm:w-24'}`}></div>
                      <p className={`font-serif font-bold text-slate-700 ${
                        allPanelsCollapsed ? 'text-sm sm:text-2xl' : 'text-lg sm:text-2xl'
                      }`}>PortfolioASaurus Ltd.</p>
                      <div className={`h-px bg-slate-400 ${allPanelsCollapsed ? 'w-8 sm:w-24' : 'w-12 sm:w-24'}`}></div>
                    </div>
                    <p className={`text-slate-500 pt-1 sm:pt-2 ${
                      allPanelsCollapsed ? 'text-[6px] sm:text-[10px]' : 'text-[8px] sm:text-[10px]'
                    }`}>Certificate No. PA-2024-001 • Par Value: $0.001 • Authorized Capital: Unlimited</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
      <Footer />
      
      {/* Inactivity Warning - Auto logout based on user's security setting */}
      {isAuthenticated && (
        <InactivityWarning 
          onLogout={handleLogout}
          timeoutMinutes={autoLogoutMinutes}
          warningMinutes={1}
        />
      )}
      {/* Changelog Modal */}
      {showChangelog && (
        <VersionChangelog 
          currentVersion={footerVersion.replace('v', '')} 
          onClose={() => setShowChangelog(false)} 
        />
      )}
    </div>
    </>
  );
};

export default PortfolioDashboard;